// src/components/Button/Button.tsx
import { jsx } from "react/jsx-runtime";
function Button({
  variant = "primary",
  size = "md",
  className,
  type = "button",
  ...props
}) {
  const classes = ["iv-button", `iv-button--${variant}`, `iv-button--${size}`, className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx("button", { ...props, type, className: classes });
}

// src/components/Input/Input.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
function Input({ error = false, className, type = "text", ...props }) {
  const classes = ["iv-input", error ? "iv-input--error" : null, className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx2("input", { ...props, type, className: classes });
}

// src/components/Textarea/Textarea.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
function Textarea({ error = false, className, rows = 3, ...props }) {
  const classes = ["iv-textarea", error ? "iv-textarea--error" : null, className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx3("textarea", { ...props, rows, className: classes });
}

// src/components/Select/Select.tsx
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";

// src/components/Select/selectNav.ts
function clampIndex(index, length) {
  if (length <= 0) return -1;
  return Math.max(0, Math.min(index, length - 1));
}
function stepIndex(current, delta, length) {
  if (length <= 0) return -1;
  return clampIndex(current + delta, length);
}
function typeAheadIndex(options, buffer, from) {
  const q = buffer.toLowerCase();
  const n = options.length;
  if (!q || n === 0) return -1;
  const origin = from < 0 ? 0 : from % n;
  const startOffset = buffer.length > 1 ? 0 : 1;
  for (let step = startOffset; step < n + startOffset; step++) {
    const i = (origin + step) % n;
    if (options[i].label.toLowerCase().startsWith(q)) return i;
  }
  return -1;
}

// src/components/Select/Select.tsx
import { jsx as jsx4, jsxs } from "react/jsx-runtime";
var VIEWPORT_MARGIN = 8;
var TRIGGER_GAP = 4;
function computeMenuPosition(triggerRect, menuHeight) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const spaceBelow = vh - triggerRect.bottom - TRIGGER_GAP - VIEWPORT_MARGIN;
  const spaceAbove = triggerRect.top - TRIGGER_GAP - VIEWPORT_MARGIN;
  const placement = menuHeight <= spaceBelow || spaceBelow >= spaceAbove ? "below" : "above";
  const room = placement === "below" ? spaceBelow : spaceAbove;
  const maxHeight = Math.max(0, Math.min(menuHeight, room));
  const top = placement === "below" ? triggerRect.bottom + TRIGGER_GAP : triggerRect.top - TRIGGER_GAP - maxHeight;
  let left = triggerRect.left;
  const width = triggerRect.width;
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - width - VIEWPORT_MARGIN));
  return { top, left, width, maxHeight, placement };
}
function Select({
  value,
  onChange,
  options,
  placeholder = "Select\u2026",
  disabled = false,
  className,
  id,
  "aria-label": ariaLabel
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [coords, setCoords] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const typeaheadRef = useRef({ buffer: "", timer: null });
  const listId = useId();
  const optionId = useId();
  const selected = options.find((o) => o.value === value);
  const selectedIndex = Math.max(0, options.findIndex((o) => o.value === value));
  useEffect(() => {
    if (open) setActive(selectedIndex);
  }, [open, selectedIndex]);
  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;
    const triggerRect = trigger.getBoundingClientRect();
    setCoords(computeMenuPosition(triggerRect, menu.scrollHeight));
  }, []);
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, reposition]);
  useEffect(() => {
    if (!open || !coords) return;
    const menu = menuRef.current;
    const el = menu?.querySelector(`[data-index="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open, coords]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const choose = useCallback(
    (i) => {
      const opt = options[i];
      if (opt) {
        onChange(opt.value);
        setOpen(false);
        triggerRef.current?.focus();
      }
    },
    [onChange, options]
  );
  const runTypeAhead = useCallback(
    (key) => {
      const ta = typeaheadRef.current;
      if (ta.timer) window.clearTimeout(ta.timer);
      ta.buffer += key;
      const target = typeAheadIndex(options, ta.buffer, active);
      if (target >= 0) setActive(target);
      ta.timer = window.setTimeout(() => {
        ta.buffer = "";
        ta.timer = null;
      }, 500);
    },
    [active, options]
  );
  function onKeyDown(e) {
    if (disabled) return;
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        setOpen(false);
        break;
      case "Tab":
        setOpen(false);
        break;
      case "ArrowDown":
        e.preventDefault();
        setActive((a) => stepIndex(a, 1, options.length));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActive((a) => stepIndex(a, -1, options.length));
        break;
      case "Home":
        e.preventDefault();
        setActive(0);
        break;
      case "End":
        e.preventDefault();
        setActive(options.length - 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        choose(active);
        break;
      default:
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
          e.preventDefault();
          runTypeAhead(e.key);
        }
    }
  }
  const classes = ["iv-select", open && "iv-select--open", disabled && "iv-select--disabled", className].filter(Boolean).join(" ");
  const menuStyle = coords ? {
    position: "fixed",
    top: coords.top,
    left: coords.left,
    width: coords.width,
    maxHeight: coords.maxHeight || void 0
  } : { position: "fixed", top: -9999, left: -9999, visibility: "hidden" };
  const activeId = open && options[active] ? `${optionId}-${active}` : void 0;
  return /* @__PURE__ */ jsxs("div", { ref: rootRef, className: classes, children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        ref: triggerRef,
        type: "button",
        className: "iv-select__trigger",
        id,
        disabled,
        "aria-haspopup": "listbox",
        "aria-expanded": open,
        "aria-controls": open ? listId : void 0,
        "aria-activedescendant": activeId,
        "aria-label": ariaLabel,
        onClick: () => !disabled && setOpen((o) => !o),
        onKeyDown,
        children: [
          /* @__PURE__ */ jsx4("span", { className: `iv-select__value${selected ? "" : " iv-select__value--placeholder"}`, children: selected ? selected.label : placeholder }),
          /* @__PURE__ */ jsx4("span", { className: "iv-select__chevron", "aria-hidden": "true" })
        ]
      }
    ),
    open && typeof document !== "undefined" && createPortal(
      /* @__PURE__ */ jsx4(
        "ul",
        {
          ref: menuRef,
          className: `iv-select__menu${coords ? ` iv-select__menu--${coords.placement}` : ""}`,
          role: "listbox",
          id: listId,
          tabIndex: -1,
          "data-open": coords ? "true" : void 0,
          style: menuStyle,
          children: options.map((opt, i) => {
            const optClasses = [
              "iv-select__option",
              i === active && "iv-select__option--active",
              opt.value === value && "iv-select__option--selected"
            ].filter(Boolean).join(" ");
            return /* @__PURE__ */ jsx4(
              "li",
              {
                id: `${optionId}-${i}`,
                "data-index": i,
                role: "option",
                "aria-selected": opt.value === value,
                className: optClasses,
                onMouseEnter: () => setActive(i),
                onMouseDown: (e) => {
                  e.preventDefault();
                  choose(i);
                },
                children: /* @__PURE__ */ jsx4("span", { className: "iv-select__option-label", children: opt.label })
              },
              opt.value
            );
          })
        }
      ),
      document.body
    )
  ] });
}

// src/components/DatePicker/DatePicker.tsx
import {
  useCallback as useCallback2,
  useEffect as useEffect3,
  useId as useId2,
  useLayoutEffect as useLayoutEffect2,
  useRef as useRef3,
  useState as useState3
} from "react";
import { createPortal as createPortal2 } from "react-dom";

// src/components/DatePicker/Calendar.tsx
import { useEffect as useEffect2, useMemo, useRef as useRef2, useState as useState2 } from "react";

// src/components/DatePicker/dateMath.ts
var ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
function isLeapYear(y) {
  return y % 4 === 0 && y % 100 !== 0 || y % 400 === 0;
}
function daysInMonth(y, m) {
  if (m === 2) return isLeapYear(y) ? 29 : 28;
  return m === 4 || m === 6 || m === 9 || m === 11 ? 30 : 31;
}
function parseISO(value) {
  if (!value) return null;
  const match = ISO_RE.exec(value);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (m < 1 || m > 12 || d < 1 || d > daysInMonth(y, m)) return null;
  return { y, m, d };
}
function formatISO({ y, m, d }) {
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
function toDayNumber({ y, m, d }) {
  const yy = m <= 2 ? y - 1 : y;
  const era = Math.floor(yy / 400);
  const yoe = yy - era * 400;
  const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}
function fromDayNumber(n) {
  const z = n + 719468;
  const era = Math.floor(z / 146097);
  const doe = z - era * 146097;
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const m = mp < 10 ? mp + 3 : mp - 9;
  return { y: yoe + era * 400 + (m <= 2 ? 1 : 0), m, d };
}
function weekdayOf(date) {
  return ((toDayNumber(date) + 4) % 7 + 7) % 7;
}
function addDays(date, delta) {
  return fromDayNumber(toDayNumber(date) + delta);
}
function addMonths(date, delta) {
  const index = date.y * 12 + (date.m - 1) + delta;
  const y = Math.floor(index / 12);
  const m = index - y * 12 + 1;
  return { y, m, d: Math.min(date.d, daysInMonth(y, m)) };
}
function compareYMD(a, b) {
  return toDayNumber(a) - toDayNumber(b);
}
function clampYMD(date, min, max) {
  if (min && compareYMD(date, min) < 0) return min;
  if (max && compareYMD(date, max) > 0) return max;
  return date;
}
function isOutside(date, min, max) {
  return !!min && compareYMD(date, min) < 0 || !!max && compareYMD(date, max) > 0;
}
function buildMonthGrid(y, m, weekStartsOn) {
  const first = { y, m, d: 1 };
  const lead = (weekdayOf(first) - weekStartsOn + 7) % 7;
  const start = toDayNumber(first) - lead;
  const weeks = [];
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      const day = fromDayNumber(start + w * 7 + i);
      week.push({ ...day, iso: formatISO(day), inMonth: day.y === y && day.m === m });
    }
    weeks.push(week);
  }
  return weeks;
}
function weekEdge(date, weekStartsOn, edge) {
  const offset = (weekdayOf(date) - weekStartsOn + 7) % 7;
  return addDays(date, edge === "start" ? -offset : 6 - offset);
}
function todayYMD(now = /* @__PURE__ */ new Date()) {
  return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
}
var EARLIEST = { y: 1, m: 1, d: 1 };
var LATEST = { y: 9999, m: 12, d: 31 };
function clampSupported(date) {
  if (date.y < EARLIEST.y) return EARLIEST;
  if (date.y > LATEST.y) return LATEST;
  return date;
}
var warned = /* @__PURE__ */ new Set();
function warnOnce(message) {
  const env = globalThis.process?.env;
  if (env?.NODE_ENV === "production" || warned.has(message)) return;
  warned.add(message);
  console.warn(`[ironvale] ${message}`);
}
function parseBound(value, name) {
  const parsed = parseISO(value);
  if (value && !parsed) warnOnce(`DatePicker: ${name}="${value}" is not a YYYY-MM-DD day; ignored.`);
  return parsed;
}
function displayDate({ y, m, d }) {
  const date = new Date(2e3, 0, 1, 12);
  date.setFullYear(y, m - 1, d);
  return date;
}
function formatReadable(date, locale) {
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric"
  }).format(displayDate(date));
}
function formatLong(date, locale) {
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric"
  }).format(displayDate(date));
}
function formatMonthTitle(y, m, locale) {
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(
    displayDate({ y, m, d: 1 })
  );
}
function weekdayNames(weekStartsOn, locale) {
  const short = new Intl.DateTimeFormat(locale, { weekday: "short" });
  const long = new Intl.DateTimeFormat(locale, { weekday: "long" });
  const sunday = { y: 2023, m: 1, d: 1 };
  return Array.from({ length: 7 }, (_, i) => {
    const day = displayDate(addDays(sunday, i + weekStartsOn));
    return { short: short.format(day), long: long.format(day) };
  });
}

// src/components/DatePicker/Calendar.tsx
import { jsx as jsx5, jsxs as jsxs2 } from "react/jsx-runtime";
function Calendar({
  value,
  onSelect,
  min,
  max,
  weekStartsOn = 1,
  autoFocus = false,
  "aria-label": ariaLabel,
  className
}) {
  const selected = parseISO(value);
  const minDay = parseBound(min, "min");
  const maxDay = parseBound(max, "max");
  const today = todayYMD();
  const emptyRange = !!minDay && !!maxDay && compareYMD(minDay, maxDay) > 0;
  if (emptyRange) warnOnce(`DatePicker: min="${min}" is after max="${max}"; no day can be chosen.`);
  const bound = (day) => emptyRange ? clampSupported(day) : clampYMD(clampSupported(day), minDay, maxDay);
  const [focused, setFocused] = useState2(() => bound(selected ?? today));
  const gridRef = useRef2(null);
  const titleRef = useRef2(null);
  const wantsFocus = useRef2(autoFocus);
  useEffect2(() => {
    const next = parseISO(value);
    if (next) setFocused(bound(next));
  }, [value]);
  const focusedISO = formatISO(focused);
  useEffect2(() => {
    if (!wantsFocus.current) return;
    wantsFocus.current = false;
    const target = emptyRange ? titleRef.current : gridRef.current?.querySelector(`[data-date="${focusedISO}"]`);
    target?.focus({ preventScroll: true });
  });
  const weeks = useMemo(
    () => buildMonthGrid(focused.y, focused.m, weekStartsOn),
    [focused.y, focused.m, weekStartsOn]
  );
  const names = useMemo(() => weekdayNames(weekStartsOn), [weekStartsOn]);
  const title = formatMonthTitle(focused.y, focused.m);
  const isDisabled = (day) => emptyRange || isOutside(day, minDay, maxDay) || day.y < 1 || day.y > 9999;
  const move = (next) => {
    wantsFocus.current = true;
    setFocused(bound(next));
  };
  const page = (delta) => {
    setFocused((f) => bound(addMonths(f, delta)));
  };
  const choose = (day) => {
    if (isDisabled(day)) return;
    wantsFocus.current = true;
    setFocused(day);
    onSelect(formatISO(day));
  };
  function onKeyDown(e) {
    let next = null;
    switch (e.key) {
      case "ArrowLeft":
        next = addDays(focused, -1);
        break;
      case "ArrowRight":
        next = addDays(focused, 1);
        break;
      case "ArrowUp":
        next = addDays(focused, -7);
        break;
      case "ArrowDown":
        next = addDays(focused, 7);
        break;
      case "Home":
        next = weekEdge(focused, weekStartsOn, "start");
        break;
      case "End":
        next = weekEdge(focused, weekStartsOn, "end");
        break;
      case "PageUp":
        next = addMonths(focused, e.shiftKey ? -12 : -1);
        break;
      case "PageDown":
        next = addMonths(focused, e.shiftKey ? 12 : 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        choose(focused);
        return;
      default:
        return;
    }
    e.preventDefault();
    move(next);
  }
  const prevMonthEnd = addDays({ y: focused.y, m: focused.m, d: 1 }, -1);
  const nextMonthStart = addDays(addMonths({ y: focused.y, m: focused.m, d: 1 }, 1), 0);
  const prevDisabled = emptyRange || isOutside(prevMonthEnd, minDay, null) || prevMonthEnd.y < 1;
  const nextDisabled = emptyRange || isOutside(nextMonthStart, null, maxDay) || nextMonthStart.y > 9999;
  return /* @__PURE__ */ jsxs2("div", { className: ["iv-calendar", className].filter(Boolean).join(" "), children: [
    /* @__PURE__ */ jsxs2("div", { className: "iv-calendar__head", children: [
      /* @__PURE__ */ jsx5(
        "button",
        {
          type: "button",
          className: "iv-calendar__nav",
          "aria-label": "Previous month",
          "aria-disabled": prevDisabled || void 0,
          onClick: (e) => {
            e.currentTarget.focus({ preventScroll: true });
            if (!prevDisabled) page(-1);
          },
          children: /* @__PURE__ */ jsx5("span", { className: "iv-calendar__chevron iv-calendar__chevron--prev", "aria-hidden": "true" })
        }
      ),
      /* @__PURE__ */ jsx5(
        "div",
        {
          ref: titleRef,
          className: "iv-calendar__title",
          "aria-live": "polite",
          "aria-atomic": "true",
          tabIndex: -1,
          children: title
        }
      ),
      /* @__PURE__ */ jsx5(
        "button",
        {
          type: "button",
          className: "iv-calendar__nav",
          "aria-label": "Next month",
          "aria-disabled": nextDisabled || void 0,
          onClick: (e) => {
            e.currentTarget.focus({ preventScroll: true });
            if (!nextDisabled) page(1);
          },
          children: /* @__PURE__ */ jsx5("span", { className: "iv-calendar__chevron iv-calendar__chevron--next", "aria-hidden": "true" })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs2(
      "table",
      {
        ref: gridRef,
        className: "iv-calendar__grid",
        role: "grid",
        "aria-label": ariaLabel ?? title,
        onKeyDown,
        children: [
          /* @__PURE__ */ jsx5("thead", { children: /* @__PURE__ */ jsx5("tr", { children: names.map((n) => /* @__PURE__ */ jsx5("th", { scope: "col", abbr: n.long, className: "iv-calendar__weekday", children: n.short }, n.long)) }) }),
          /* @__PURE__ */ jsx5("tbody", { children: weeks.map((week) => /* @__PURE__ */ jsx5("tr", { children: week.map((day) => {
            const disabled = isDisabled(day);
            const isSelected = !!selected && day.iso === value;
            const isToday = day.y === today.y && day.m === today.m && day.d === today.d;
            const isFocused = day.iso === focusedISO;
            const classes = [
              "iv-calendar__day",
              !day.inMonth && "iv-calendar__day--outside",
              isToday && "iv-calendar__day--today",
              isSelected && "iv-calendar__day--selected",
              disabled && "iv-calendar__day--disabled"
            ].filter(Boolean).join(" ");
            return /* @__PURE__ */ jsx5(
              "td",
              {
                role: "gridcell",
                "data-date": day.iso,
                className: classes,
                tabIndex: isFocused && !emptyRange ? 0 : -1,
                "aria-selected": isSelected,
                "aria-current": isToday ? "date" : void 0,
                "aria-disabled": disabled || void 0,
                "aria-label": formatLong(day),
                onClick: () => choose(day),
                children: day.d
              },
              day.iso
            );
          }) }, week[0].iso)) })
        ]
      }
    )
  ] });
}

// src/components/DatePicker/DatePicker.tsx
import { jsx as jsx6, jsxs as jsxs3 } from "react/jsx-runtime";
var VIEWPORT_MARGIN2 = 8;
var TRIGGER_GAP2 = 4;
function computePopoverPosition(triggerRect, popover, viewport) {
  const spaceBelow = viewport.height - triggerRect.bottom - TRIGGER_GAP2 - VIEWPORT_MARGIN2;
  const spaceAbove = triggerRect.top - TRIGGER_GAP2 - VIEWPORT_MARGIN2;
  const placement = popover.height <= spaceBelow || spaceBelow >= spaceAbove ? "below" : "above";
  const rawTop = placement === "below" ? triggerRect.bottom + TRIGGER_GAP2 : triggerRect.top - TRIGGER_GAP2 - popover.height;
  const top = Math.max(
    VIEWPORT_MARGIN2,
    Math.min(rawTop, viewport.height - popover.height - VIEWPORT_MARGIN2)
  );
  const left = Math.max(
    VIEWPORT_MARGIN2,
    Math.min(triggerRect.left, viewport.width - popover.width - VIEWPORT_MARGIN2)
  );
  return { top, left, placement };
}
function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date\u2026",
  disabled = false,
  min,
  max,
  clearable = true,
  weekStartsOn = 1,
  className,
  id,
  "aria-label": ariaLabel
}) {
  const [open, setOpen] = useState3(false);
  const [coords, setCoords] = useState3(null);
  const rootRef = useRef3(null);
  const triggerRef = useRef3(null);
  const popoverRef = useRef3(null);
  const dialogId = useId2();
  const selected = parseISO(value);
  const minDay = parseBound(min, "min");
  const maxDay = parseBound(max, "max");
  const todayDisabled = isOutside(todayYMD(), minDay, maxDay);
  const close = useCallback2((returnFocus) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);
  const reposition = useCallback2(() => {
    const trigger = triggerRef.current;
    const popover = popoverRef.current;
    if (!trigger || !popover) return;
    const rect = trigger.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight || rect.right < 0 || rect.left > window.innerWidth) {
      close(false);
      return;
    }
    setCoords(
      computePopoverPosition(
        rect,
        { width: popover.offsetWidth, height: popover.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight }
      )
    );
  }, [close]);
  useLayoutEffect2(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, reposition]);
  useEffect3(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);
  useEffect3(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      close(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, close]);
  const commit = (next) => {
    if (disabled) return;
    if (next !== (selected ? value : "")) onChange(next);
    close(true);
  };
  function onTriggerKeyDown(e) {
    if (disabled) return;
    if (open) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        close(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
    }
  }
  function onPopoverKeyDown(e) {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close(true);
      return;
    }
    if (e.key !== "Tab") return;
    const stops = Array.from(
      popoverRef.current?.querySelectorAll(
        'button:not([disabled]):not([aria-disabled="true"]), [tabindex="0"]'
      ) ?? []
    );
    if (stops.length === 0) return;
    const first = stops[0];
    const last = stops[stops.length - 1];
    const current = document.activeElement;
    if (e.shiftKey && current === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && current === last) {
      e.preventDefault();
      first.focus();
    }
  }
  const readable = selected ? formatReadable(selected) : placeholder;
  const classes = [
    "iv-datepicker",
    open && "iv-datepicker--open",
    disabled && "iv-datepicker--disabled",
    className
  ].filter(Boolean).join(" ");
  const popoverStyle = coords ? { position: "fixed", top: coords.top, left: coords.left } : (
    // Unmeasured for one frame: kept in the layout and FOCUSABLE (the stylesheet
    // holds it at opacity 0 until `data-open`) — `visibility: hidden` here made
    // the calendar's focus-on-open a silent no-op in every real browser.
    { position: "fixed", top: 0, left: 0, pointerEvents: "none" }
  );
  return /* @__PURE__ */ jsxs3("div", { ref: rootRef, className: classes, children: [
    /* @__PURE__ */ jsxs3(
      "button",
      {
        ref: triggerRef,
        type: "button",
        className: "iv-datepicker__trigger",
        id,
        disabled,
        "aria-haspopup": "dialog",
        "aria-expanded": open,
        "aria-controls": open ? dialogId : void 0,
        "aria-label": ariaLabel ? `${ariaLabel}: ${readable}` : void 0,
        onClick: () => !disabled && setOpen((o) => !o),
        onKeyDown: onTriggerKeyDown,
        children: [
          /* @__PURE__ */ jsx6(
            "span",
            {
              className: `iv-datepicker__value${selected ? "" : " iv-datepicker__value--placeholder"}`,
              children: readable
            }
          ),
          /* @__PURE__ */ jsx6("span", { className: "iv-datepicker__icon", "aria-hidden": "true" })
        ]
      }
    ),
    open && !disabled && typeof document !== "undefined" && createPortal2(
      /* @__PURE__ */ jsxs3(
        "div",
        {
          ref: popoverRef,
          className: `iv-datepicker__popover${coords ? ` iv-datepicker__popover--${coords.placement}` : ""}`,
          role: "dialog",
          id: dialogId,
          "aria-label": ariaLabel ? `${ariaLabel} \u2014 choose a date` : "Choose a date",
          "data-open": coords ? "true" : void 0,
          tabIndex: -1,
          style: popoverStyle,
          onKeyDown: onPopoverKeyDown,
          children: [
            /* @__PURE__ */ jsx6(
              Calendar,
              {
                value: selected ? value : "",
                onSelect: commit,
                min,
                max,
                weekStartsOn,
                autoFocus: true
              }
            ),
            /* @__PURE__ */ jsxs3("div", { className: "iv-datepicker__foot", children: [
              /* @__PURE__ */ jsx6(
                "button",
                {
                  type: "button",
                  className: "iv-datepicker__action",
                  disabled: todayDisabled,
                  onClick: () => {
                    const now = todayYMD();
                    if (!isOutside(now, minDay, maxDay)) commit(formatISO(now));
                  },
                  children: "Today"
                }
              ),
              clearable && /* @__PURE__ */ jsx6(
                "button",
                {
                  type: "button",
                  className: "iv-datepicker__action",
                  disabled: !selected,
                  onClick: () => commit(""),
                  children: "Clear"
                }
              )
            ] })
          ]
        }
      ),
      document.body
    )
  ] });
}

// src/components/ThemeToggle/ThemeToggle.tsx
import { useState as useState4 } from "react";

// src/colorMode.ts
var KEY = "diwata-mode";
function getColorMode() {
  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem(KEY);
    if (stored === "light" || stored === "dark") return stored;
  }
  return "dark";
}
function applyColorMode(mode) {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-mode", mode);
  }
}
function setColorMode(mode) {
  if (typeof localStorage !== "undefined") localStorage.setItem(KEY, mode);
  applyColorMode(mode);
}
function initColorMode() {
  const mode = getColorMode();
  applyColorMode(mode);
  return mode;
}
function toggleColorMode() {
  const next = getColorMode() === "dark" ? "light" : "dark";
  setColorMode(next);
  return next;
}

// src/components/ThemeToggle/ThemeToggle.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
function ThemeToggle({ className }) {
  const [mode, setMode] = useState4(() => getColorMode());
  const classes = ["iv-theme-toggle", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx7(
    "button",
    {
      type: "button",
      className: classes,
      "aria-label": mode === "dark" ? "Switch to light mode" : "Switch to dark mode",
      title: mode === "dark" ? "Light mode" : "Dark mode",
      onClick: () => setMode(toggleColorMode()),
      children: /* @__PURE__ */ jsx7("span", { "aria-hidden": "true", children: mode === "dark" ? "\u263E" : "\u2600" })
    }
  );
}

// src/fontPreset.ts
var KEY2 = "diwata-font";
function getFontPreset() {
  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem(KEY2);
    if (stored === "default" || stored === "serif" || stored === "mono") return stored;
  }
  return "default";
}
function applyFontPreset(preset) {
  if (typeof document === "undefined") return;
  if (preset === "default") document.documentElement.removeAttribute("data-font");
  else document.documentElement.setAttribute("data-font", preset);
}
function setFontPreset(preset) {
  if (typeof localStorage !== "undefined") localStorage.setItem(KEY2, preset);
  applyFontPreset(preset);
}
function initFontPreset() {
  const preset = getFontPreset();
  applyFontPreset(preset);
  return preset;
}

// src/themePreset.ts
var THEME_PRESETS = [
  { value: "default", label: "Default", nativeMode: null },
  { value: "nocturne", label: "Nocturne", nativeMode: "dark" },
  { value: "parchment", label: "Parchment", nativeMode: "light" },
  { value: "midnight", label: "Midnight", nativeMode: "dark" },
  { value: "aurora", label: "Aurora", nativeMode: "dark" },
  { value: "ember", label: "Ember", nativeMode: "dark" },
  { value: "high-contrast", label: "Contrast", nativeMode: "dark" }
];
var VALUES = new Set(THEME_PRESETS.map((p) => p.value));
var KEY3 = "diwata-preset";
function isPreset(value) {
  return value !== null && VALUES.has(value);
}
function presetNativeMode(preset) {
  return THEME_PRESETS.find((p) => p.value === preset)?.nativeMode ?? null;
}
function getThemePreset() {
  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem(KEY3);
    if (isPreset(stored)) return stored;
  }
  return "default";
}
function applyThemePreset(preset) {
  if (typeof document === "undefined") return;
  if (preset === "default") {
    document.documentElement.removeAttribute("data-preset");
    return;
  }
  document.documentElement.setAttribute("data-preset", preset);
  const native = presetNativeMode(preset);
  if (native) setColorMode(native);
}
function setThemePreset(preset) {
  if (typeof localStorage !== "undefined") localStorage.setItem(KEY3, preset);
  applyThemePreset(preset);
}
function initThemePreset() {
  const preset = getThemePreset();
  applyThemePreset(preset);
  return preset;
}

// src/textScale.ts
var TEXT_SCALES = [
  { value: "sm", label: "S" },
  { value: "md", label: "M" },
  { value: "lg", label: "L" },
  { value: "xl", label: "XL" }
];
var VALUES2 = new Set(TEXT_SCALES.map((s) => s.value));
var KEY4 = "diwata-text-scale";
function isTextScale(value) {
  return value !== null && VALUES2.has(value);
}
function getTextScale() {
  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem(KEY4);
    if (isTextScale(stored)) return stored;
  }
  return "md";
}
function applyTextScale(scale) {
  if (typeof document === "undefined") return;
  if (scale === "md") document.documentElement.removeAttribute("data-text-scale");
  else document.documentElement.setAttribute("data-text-scale", scale);
}
function setTextScale(scale) {
  if (typeof localStorage !== "undefined") localStorage.setItem(KEY4, scale);
  applyTextScale(scale);
}
function initTextScale() {
  const scale = getTextScale();
  applyTextScale(scale);
  return scale;
}

// src/accent.ts
var ACCENTS = [
  { value: "gold", label: "Gold", token: "var(--ae-color-gold-500)" },
  { value: "crimson", label: "Crimson", token: "var(--ae-color-crimson-500)" },
  { value: "amber", label: "Amber", token: "var(--ae-color-yellow-500)" },
  { value: "rose", label: "Rose", token: "var(--ae-color-red-500)" },
  { value: "emerald", label: "Emerald", token: "var(--ae-color-green-500)" },
  { value: "azure", label: "Azure", token: "var(--ae-color-blue-500)" },
  { value: "violet", label: "Violet", token: "var(--ae-color-purple-500)" },
  { value: "slate", label: "Slate", token: "var(--ae-color-neutral-500)" }
];
var VALUES3 = new Set(ACCENTS.map((a) => a.value));
var KEY5 = "diwata-accent";
function isAccent(value) {
  return value !== null && (value === "auto" || VALUES3.has(value));
}
function getAccent() {
  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem(KEY5);
    if (isAccent(stored)) return stored;
  }
  return "auto";
}
function applyAccentSwatch(accent) {
  if (typeof document === "undefined") return;
  if (accent === "auto") document.documentElement.removeAttribute("data-accent");
  else document.documentElement.setAttribute("data-accent", accent);
}
function setAccent(accent) {
  if (typeof localStorage !== "undefined") localStorage.setItem(KEY5, accent);
  applyAccentSwatch(accent);
}
function initAccent() {
  const accent = getAccent();
  applyAccentSwatch(accent);
  return accent;
}
function applyAccent(color, opts = {}) {
  if (typeof document === "undefined") return;
  const style = document.documentElement.style;
  style.setProperty("--ae-color-accent", color);
  style.setProperty("--ae-color-accent-hover", opts.hover ?? color);
  if (opts.text !== void 0) style.setProperty("--ae-color-accent-text", opts.text);
}
function clearAccent() {
  if (typeof document === "undefined") return;
  const style = document.documentElement.style;
  style.removeProperty("--ae-color-accent");
  style.removeProperty("--ae-color-accent-hover");
  style.removeProperty("--ae-color-accent-text");
}

// src/contrast.ts
var KEY6 = "diwata-contrast";
function getContrast() {
  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem(KEY6);
    if (stored === "normal" || stored === "high") return stored;
  }
  return "normal";
}
function applyContrast(contrast) {
  if (typeof document === "undefined") return;
  if (contrast === "normal") document.documentElement.removeAttribute("data-contrast");
  else document.documentElement.setAttribute("data-contrast", contrast);
}
function setContrast(contrast) {
  if (typeof localStorage !== "undefined") localStorage.setItem(KEY6, contrast);
  applyContrast(contrast);
}
function initContrast() {
  const contrast = getContrast();
  applyContrast(contrast);
  return contrast;
}
function toggleContrast() {
  const next = getContrast() === "high" ? "normal" : "high";
  setContrast(next);
  return next;
}

// src/density.ts
var KEY7 = "diwata-density";
function getDensity() {
  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem(KEY7);
    if (stored === "comfortable" || stored === "compact") return stored;
  }
  return "comfortable";
}
function applyDensity(density) {
  if (typeof document === "undefined") return;
  if (density === "comfortable") document.documentElement.removeAttribute("data-density");
  else document.documentElement.setAttribute("data-density", density);
}
function setDensity(density) {
  if (typeof localStorage !== "undefined") localStorage.setItem(KEY7, density);
  applyDensity(density);
}
function initDensity() {
  const density = getDensity();
  applyDensity(density);
  return density;
}
function toggleDensity() {
  const next = getDensity() === "compact" ? "comfortable" : "compact";
  setDensity(next);
  return next;
}

// src/reducedMotion.ts
var KEY8 = "diwata-motion";
function getMotion() {
  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem(KEY8);
    if (stored === "system" || stored === "reduced") return stored;
  }
  return "system";
}
function applyMotion(motion) {
  if (typeof document === "undefined") return;
  if (motion === "system") document.documentElement.removeAttribute("data-motion");
  else document.documentElement.setAttribute("data-motion", motion);
}
function setMotion(motion) {
  if (typeof localStorage !== "undefined") localStorage.setItem(KEY8, motion);
  applyMotion(motion);
}
function initMotion() {
  const motion = getMotion();
  applyMotion(motion);
  return motion;
}
function toggleMotion() {
  const next = getMotion() === "reduced" ? "system" : "reduced";
  setMotion(next);
  return next;
}

// src/components/AppearancePanel/AppearancePanel.tsx
import { useEffect as useEffect4, useLayoutEffect as useLayoutEffect3, useRef as useRef4, useState as useState5 } from "react";
import { createPortal as createPortal3 } from "react-dom";
import { Fragment, jsx as jsx8, jsxs as jsxs4 } from "react/jsx-runtime";
var FONTS = [
  { value: "default", label: "Default" },
  { value: "serif", label: "Serif" },
  { value: "mono", label: "Mono" }
];
var MODES = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" }
];
var CONTRASTS = [
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" }
];
var DENSITIES = [
  { value: "comfortable", label: "Cozy" },
  { value: "compact", label: "Compact" }
];
var MOTIONS = [
  { value: "system", label: "Full" },
  { value: "reduced", label: "Reduced" }
];
function AppearancePanel({ className, align = "right" }) {
  const [open, setOpen] = useState5(false);
  const [font, setFont] = useState5(() => getFontPreset());
  const [scale, setScale] = useState5(() => getTextScale());
  const [accent, setAccentState] = useState5(() => getAccent());
  const [mode, setMode] = useState5(() => getColorMode());
  const [preset, setPresetState] = useState5(() => getThemePreset());
  const [contrast, setContrastState] = useState5(() => getContrast());
  const [density, setDensityState] = useState5(() => getDensity());
  const [motion, setMotionState] = useState5(() => getMotion());
  const triggerRef = useRef4(null);
  const menuRef = useRef4(null);
  const [pos, setPos] = useState5(null);
  useEffect4(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  useLayoutEffect3(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const place = () => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;
      const r = trigger.getBoundingClientRect();
      const mw = menu.offsetWidth;
      const mh = menu.offsetHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const gap = 6;
      const margin = 8;
      let top = r.bottom + gap;
      const roomBelow = vh - r.bottom - gap - margin;
      const roomAbove = r.top - gap - margin;
      if (roomBelow < mh && roomAbove > roomBelow) top = r.top - gap - mh;
      top = Math.max(margin, Math.min(top, vh - mh - margin));
      let left = align === "left" ? r.left : r.right - mw;
      left = Math.max(margin, Math.min(left, vw - mw - margin));
      setPos({ top, left });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, align]);
  const classes = ["iv-appearance", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs4("div", { className: classes, children: [
    /* @__PURE__ */ jsx8(
      "button",
      {
        ref: triggerRef,
        type: "button",
        className: "iv-appearance__trigger",
        "aria-haspopup": "dialog",
        "aria-expanded": open,
        "aria-label": "Appearance",
        title: "Appearance",
        onClick: () => setOpen((o) => !o),
        children: /* @__PURE__ */ jsx8("span", { "aria-hidden": "true", children: "Aa" })
      }
    ),
    open && createPortal3(
      /* @__PURE__ */ jsxs4(
        "div",
        {
          ref: menuRef,
          className: "iv-appearance__menu",
          role: "dialog",
          "aria-label": "Appearance",
          style: {
            top: pos ? `${pos.top}px` : 0,
            left: pos ? `${pos.left}px` : 0,
            visibility: pos ? "visible" : "hidden"
          },
          children: [
            /* @__PURE__ */ jsxs4("div", { className: "iv-appearance__group iv-appearance__group--wide", children: [
              /* @__PURE__ */ jsx8("span", { className: "iv-appearance__label", children: "Theme" }),
              /* @__PURE__ */ jsx8(
                "div",
                {
                  className: "iv-appearance__seg iv-appearance__seg--wrap",
                  role: "radiogroup",
                  "aria-label": "Theme preset",
                  children: THEME_PRESETS.map((p) => /* @__PURE__ */ jsx8(
                    "button",
                    {
                      type: "button",
                      role: "radio",
                      "aria-checked": preset === p.value,
                      title: p.nativeMode ? `${p.label} (${p.nativeMode})` : p.label,
                      className: `iv-appearance__opt${preset === p.value ? " iv-appearance__opt--on" : ""}`,
                      onClick: () => {
                        setThemePreset(p.value);
                        setPresetState(p.value);
                        if (p.nativeMode) setMode(p.nativeMode);
                      },
                      children: p.label
                    },
                    p.value
                  ))
                }
              )
            ] }),
            /* @__PURE__ */ jsxs4("div", { className: "iv-appearance__group iv-appearance__group--wide", children: [
              /* @__PURE__ */ jsx8("span", { className: "iv-appearance__label", children: "Font" }),
              /* @__PURE__ */ jsx8("div", { className: "iv-appearance__seg", role: "radiogroup", "aria-label": "Font preset", children: FONTS.map((f) => /* @__PURE__ */ jsx8(
                "button",
                {
                  type: "button",
                  role: "radio",
                  "aria-checked": font === f.value,
                  className: `iv-appearance__opt iv-appearance__opt--font-${f.value}${font === f.value ? " iv-appearance__opt--on" : ""}`,
                  onClick: () => {
                    setFontPreset(f.value);
                    setFont(f.value);
                  },
                  children: f.label
                },
                f.value
              )) })
            ] }),
            /* @__PURE__ */ jsxs4("div", { className: "iv-appearance__group", children: [
              /* @__PURE__ */ jsx8("span", { className: "iv-appearance__label", children: "Text size" }),
              /* @__PURE__ */ jsx8("div", { className: "iv-appearance__seg", role: "radiogroup", "aria-label": "Text size", children: TEXT_SCALES.map((s) => /* @__PURE__ */ jsx8(
                "button",
                {
                  type: "button",
                  role: "radio",
                  "aria-checked": scale === s.value,
                  "aria-label": s.value,
                  className: `iv-appearance__opt${scale === s.value ? " iv-appearance__opt--on" : ""}`,
                  onClick: () => {
                    setTextScale(s.value);
                    setScale(s.value);
                  },
                  children: s.label
                },
                s.value
              )) })
            ] }),
            /* @__PURE__ */ jsxs4("div", { className: "iv-appearance__group iv-appearance__group--wide", children: [
              /* @__PURE__ */ jsx8("span", { className: "iv-appearance__label", children: "Accent" }),
              /* @__PURE__ */ jsxs4("div", { className: "iv-appearance__swatches", role: "radiogroup", "aria-label": "Accent color", children: [
                /* @__PURE__ */ jsx8(
                  "button",
                  {
                    type: "button",
                    role: "radio",
                    "aria-checked": accent === "auto",
                    "aria-label": "Auto \u2014 theme's accent",
                    title: "Auto \u2014 theme's accent",
                    className: `iv-appearance__swatch iv-appearance__swatch--auto${accent === "auto" ? " iv-appearance__swatch--on" : ""}`,
                    onClick: () => {
                      setAccent("auto");
                      setAccentState("auto");
                    },
                    children: /* @__PURE__ */ jsx8("span", { "aria-hidden": "true", children: "A" })
                  }
                ),
                ACCENTS.map((a) => /* @__PURE__ */ jsx8(
                  "button",
                  {
                    type: "button",
                    role: "radio",
                    "aria-checked": accent === a.value,
                    "aria-label": a.label,
                    title: a.label,
                    className: `iv-appearance__swatch${accent === a.value ? " iv-appearance__swatch--on" : ""}`,
                    style: { ["--iv-swatch"]: a.token },
                    onClick: () => {
                      setAccent(a.value);
                      setAccentState(a.value);
                    }
                  },
                  a.value
                ))
              ] })
            ] }),
            /* @__PURE__ */ jsxs4("div", { className: "iv-appearance__group", children: [
              /* @__PURE__ */ jsx8("span", { className: "iv-appearance__label", children: "Mode" }),
              (() => {
                const lockedMode = presetNativeMode(preset);
                const shownMode = lockedMode ?? mode;
                return /* @__PURE__ */ jsxs4(Fragment, { children: [
                  /* @__PURE__ */ jsx8("div", { className: "iv-appearance__seg", role: "radiogroup", "aria-label": "Color mode", children: MODES.map((m) => /* @__PURE__ */ jsx8(
                    "button",
                    {
                      type: "button",
                      role: "radio",
                      "aria-checked": shownMode === m.value,
                      disabled: lockedMode !== null,
                      className: `iv-appearance__opt${shownMode === m.value ? " iv-appearance__opt--on" : ""}`,
                      onClick: () => {
                        setColorMode(m.value);
                        setMode(m.value);
                      },
                      children: m.label
                    },
                    m.value
                  )) }),
                  lockedMode !== null && /* @__PURE__ */ jsx8("span", { className: "iv-appearance__hint", children: "set by theme \u2014 pick Default to change" })
                ] });
              })()
            ] }),
            /* @__PURE__ */ jsxs4("div", { className: "iv-appearance__group", children: [
              /* @__PURE__ */ jsx8("span", { className: "iv-appearance__label", children: "Contrast" }),
              /* @__PURE__ */ jsx8("div", { className: "iv-appearance__seg", role: "radiogroup", "aria-label": "Contrast", children: CONTRASTS.map((c) => /* @__PURE__ */ jsx8(
                "button",
                {
                  type: "button",
                  role: "radio",
                  "aria-checked": contrast === c.value,
                  className: `iv-appearance__opt${contrast === c.value ? " iv-appearance__opt--on" : ""}`,
                  onClick: () => {
                    setContrast(c.value);
                    setContrastState(c.value);
                  },
                  children: c.label
                },
                c.value
              )) })
            ] }),
            /* @__PURE__ */ jsxs4("div", { className: "iv-appearance__group", children: [
              /* @__PURE__ */ jsx8("span", { className: "iv-appearance__label", children: "Density" }),
              /* @__PURE__ */ jsx8("div", { className: "iv-appearance__seg", role: "radiogroup", "aria-label": "Density", children: DENSITIES.map((d) => /* @__PURE__ */ jsx8(
                "button",
                {
                  type: "button",
                  role: "radio",
                  "aria-checked": density === d.value,
                  className: `iv-appearance__opt${density === d.value ? " iv-appearance__opt--on" : ""}`,
                  onClick: () => {
                    setDensity(d.value);
                    setDensityState(d.value);
                  },
                  children: d.label
                },
                d.value
              )) })
            ] }),
            /* @__PURE__ */ jsxs4("div", { className: "iv-appearance__group", children: [
              /* @__PURE__ */ jsx8("span", { className: "iv-appearance__label", children: "Motion" }),
              /* @__PURE__ */ jsx8("div", { className: "iv-appearance__seg", role: "radiogroup", "aria-label": "Motion", children: MOTIONS.map((m) => /* @__PURE__ */ jsx8(
                "button",
                {
                  type: "button",
                  role: "radio",
                  "aria-checked": motion === m.value,
                  className: `iv-appearance__opt${motion === m.value ? " iv-appearance__opt--on" : ""}`,
                  onClick: () => {
                    setMotion(m.value);
                    setMotionState(m.value);
                  },
                  children: m.label
                },
                m.value
              )) })
            ] })
          ]
        }
      ),
      document.body
    )
  ] });
}

// src/components/Stack/Stack.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
function Stack({
  direction = "vertical",
  wrap = false,
  gap = "md",
  className,
  ...props
}) {
  const classes = [
    "iv-stack",
    direction === "horizontal" ? "iv-stack--horizontal" : null,
    wrap ? "iv-stack--wrap" : null,
    `iv-stack--gap-${gap}`,
    className
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx9("div", { ...props, className: classes });
}

// src/components/Surface/Surface.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
function Surface({
  as,
  variant = "base",
  className,
  ...props
}) {
  const Component = as ?? "div";
  const classes = ["iv-surface", `iv-surface--${variant}`, className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx10(Component, { ...props, className: classes });
}

// src/components/Text/Text.tsx
import { jsx as jsx11 } from "react/jsx-runtime";
function Text({
  as,
  variant = "body",
  className,
  ...props
}) {
  const Component = as ?? "p";
  const classes = ["iv-text", `iv-text--${variant}`, className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx11(Component, { ...props, className: classes });
}

// src/components/Card/Card.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
function Card({ variant = "base", className, ...props }) {
  const classes = ["iv-card", `iv-card--${variant}`, className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx12("div", { ...props, className: classes });
}

// src/components/Dialog/Dialog.tsx
import {
  useCallback as useCallback3,
  useEffect as useEffect5,
  useId as useId3,
  useRef as useRef5
} from "react";
import { createPortal as createPortal4 } from "react-dom";
import { jsx as jsx13, jsxs as jsxs5 } from "react/jsx-runtime";
var FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])'
].join(",");
function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  closeOnOverlayClick = true,
  closeOnEsc = true,
  ariaLabel
}) {
  const panelRef = useRef5(null);
  const restoreRef = useRef5(null);
  const titleId = useId3();
  const descId = useId3();
  useEffect5(() => {
    if (!open) return;
    restoreRef.current = document.activeElement ?? null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const panel = panelRef.current;
    const firstFocusable = panel?.querySelector(FOCUSABLE);
    (firstFocusable ?? panel)?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      restoreRef.current?.focus?.();
    };
  }, [open]);
  const onKeyDown = useCallback3(
    (e) => {
      if (e.key === "Escape" && closeOnEsc) {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
      );
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === firstEl || active === panel)) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    },
    [closeOnEsc, onClose]
  );
  if (!open || typeof document === "undefined") return null;
  const onOverlayMouseDown = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) onClose();
  };
  return createPortal4(
    /* @__PURE__ */ jsx13("div", { className: "iv-dialog-overlay", onMouseDown: onOverlayMouseDown, children: /* @__PURE__ */ jsxs5(
      "div",
      {
        ref: panelRef,
        className: `iv-dialog iv-dialog--${size}`,
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": title ? titleId : void 0,
        "aria-label": title ? void 0 : ariaLabel,
        "aria-describedby": description ? descId : void 0,
        tabIndex: -1,
        onKeyDown,
        children: [
          /* @__PURE__ */ jsxs5("header", { className: "iv-dialog__header", children: [
            title ? /* @__PURE__ */ jsx13("h2", { className: "iv-dialog__title", id: titleId, children: title }) : /* @__PURE__ */ jsx13("span", {}),
            /* @__PURE__ */ jsx13("button", { type: "button", className: "iv-dialog__close", "aria-label": "Close", onClick: onClose, children: /* @__PURE__ */ jsx13("svg", { width: "14", height: "14", viewBox: "0 0 14 14", "aria-hidden": "true", focusable: "false", children: /* @__PURE__ */ jsx13(
              "path",
              {
                d: "M1 1l12 12M13 1L1 13",
                stroke: "currentColor",
                strokeWidth: "1.5",
                fill: "none",
                strokeLinecap: "round"
              }
            ) }) })
          ] }),
          /* @__PURE__ */ jsxs5("div", { className: "iv-dialog__body", children: [
            description && /* @__PURE__ */ jsx13("p", { className: "iv-dialog__description", id: descId, children: description }),
            children
          ] }),
          footer && /* @__PURE__ */ jsx13("footer", { className: "iv-dialog__footer", children: footer })
        ]
      }
    ) }),
    document.body
  );
}

// src/components/Badge/Badge.tsx
import { jsx as jsx14 } from "react/jsx-runtime";
function Badge({ variant = "neutral", className, ...props }) {
  const classes = ["iv-badge", `iv-badge--${variant}`, className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx14("span", { ...props, className: classes });
}

// src/components/NavItem/NavItem.tsx
import { jsx as jsx15, jsxs as jsxs6 } from "react/jsx-runtime";
function NavItem({ active = false, icon, label, className, ...props }) {
  const classes = [
    "iv-nav-item",
    active ? "iv-nav-item--active" : "",
    className
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs6("a", { ...props, className: classes, children: [
    icon && /* @__PURE__ */ jsx15("span", { className: "iv-nav-item__icon", children: icon }),
    /* @__PURE__ */ jsx15("span", { className: "iv-nav-item__label", children: label })
  ] });
}

// src/components/StatusDot/StatusDot.tsx
import { jsx as jsx16 } from "react/jsx-runtime";
var STATE_COLOR = {
  live: "green",
  idle: "yellow",
  error: "red",
  neutral: "grey"
};
function StatusDot({
  state,
  color,
  size = "md",
  pulse,
  label,
  className,
  ...props
}) {
  const resolvedColor = color ?? (state ? STATE_COLOR[state] : "grey");
  const resolvedPulse = pulse ?? state === "live";
  const classes = [
    "iv-dot",
    `iv-dot--${resolvedColor}`,
    size !== "md" ? `iv-dot--${size}` : "",
    resolvedPulse ? "iv-dot--pulse" : "",
    className
  ].filter(Boolean).join(" ");
  const a11y = label ? { role: "img", "aria-label": label } : { "aria-hidden": true };
  return /* @__PURE__ */ jsx16("span", { ...a11y, ...props, className: classes });
}

// src/components/StatusDot/liveness.ts
var DEFAULT_LIVENESS_THRESHOLDS = {
  live: 5 * 60 * 1e3,
  idle: 24 * 60 * 60 * 1e3
};
function toMillis(at) {
  if (at == null) return null;
  if (at instanceof Date) {
    const t2 = at.getTime();
    return Number.isNaN(t2) ? null : t2;
  }
  if (typeof at === "number") return Number.isFinite(at) ? at : null;
  const t = new Date(at).getTime();
  return Number.isNaN(t) ? null : t;
}
function livenessState(lastSeen, now = Date.now(), thresholds = DEFAULT_LIVENESS_THRESHOLDS) {
  const ms = toMillis(lastSeen);
  if (ms == null) return "neutral";
  const age = now - ms;
  if (age <= thresholds.live) return "live";
  if (age <= thresholds.idle) return "idle";
  return "neutral";
}
function freshness(at, now = Date.now()) {
  const ms = toMillis(at);
  if (ms == null) return "never";
  const diff = now - ms;
  if (diff < 6e4) return "just now";
  const mins = Math.floor(diff / 6e4);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ms).toLocaleDateString();
}

// src/components/Table/Table.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
function Table({ compact, static: isStatic, className, ...props }) {
  const classes = [
    "iv-table",
    compact ? "iv-table--compact" : "",
    isStatic ? "iv-table--static" : "",
    className
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx17("table", { ...props, className: classes });
}
function Thead(props) {
  return /* @__PURE__ */ jsx17("thead", { ...props });
}
function Tbody(props) {
  return /* @__PURE__ */ jsx17("tbody", { ...props });
}
function Tr(props) {
  return /* @__PURE__ */ jsx17("tr", { ...props });
}
function Th(props) {
  return /* @__PURE__ */ jsx17("th", { ...props });
}
function Td(props) {
  return /* @__PURE__ */ jsx17("td", { ...props });
}

// src/components/Spinner/Spinner.tsx
import { jsx as jsx18 } from "react/jsx-runtime";
function Spinner({ size = "md", className, ...props }) {
  const classes = ["iv-spinner", `iv-spinner--${size}`, className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx18("span", { ...props, className: classes, role: "status", "aria-label": "Loading" });
}

// src/components/AppShell/AppShell.tsx
import { jsx as jsx19, jsxs as jsxs7 } from "react/jsx-runtime";
function AppShell({ nav, header, statusBar, className, children, ...props }) {
  const classes = ["iv-app-shell", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs7("div", { ...props, className: classes, children: [
    header && /* @__PURE__ */ jsx19("header", { className: "iv-app-shell__header", children: header }),
    nav && /* @__PURE__ */ jsx19("nav", { className: "iv-app-shell__nav", children: nav }),
    /* @__PURE__ */ jsx19("main", { className: "iv-app-shell__main", children }),
    statusBar && /* @__PURE__ */ jsx19("div", { className: "iv-app-shell__statusbar", children: statusBar })
  ] });
}

// src/components/AppNav/AppNav.tsx
import { jsx as jsx20 } from "react/jsx-runtime";
function AppNav({ items, className }) {
  const classes = ["iv-app-nav", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx20("div", { className: classes, children: items.map((item, index) => /* @__PURE__ */ jsx20(
    NavItem,
    {
      label: item.label,
      icon: item.icon,
      active: item.active,
      href: item.href,
      onClick: item.onClick
    },
    item.href ?? `${item.label}-${index}`
  )) });
}

// src/components/ScrollArea/ScrollArea.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
function ScrollArea({ className, children, ...props }) {
  const classes = ["iv-scroll-area", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx21("div", { ...props, className: classes, children });
}

// src/components/ChatSurface/ChatSurface.tsx
import { useState as useState6 } from "react";
import { jsx as jsx22, jsxs as jsxs8 } from "react/jsx-runtime";
function ChatSurface({
  messages,
  onSend,
  placeholder = "Message your Sovereign\u2026",
  busy = false,
  className,
  value: valueProp,
  onValueChange,
  composerAccessory
}) {
  const [internalValue, setInternalValue] = useState6("");
  const isControlled = valueProp !== void 0;
  const value = isControlled ? valueProp : internalValue;
  const setValue = (next) => {
    if (isControlled) onValueChange?.(next);
    else setInternalValue(next);
  };
  const classes = ["iv-chat-surface", className].filter(Boolean).join(" ");
  const handleSubmit = (event) => {
    event.preventDefault();
    const text = value.trim();
    if (!text || busy) return;
    onSend?.(text);
    setValue("");
  };
  return /* @__PURE__ */ jsxs8("div", { className: classes, children: [
    /* @__PURE__ */ jsx22(ScrollArea, { className: "iv-chat-surface__messages", children: messages.map((message) => /* @__PURE__ */ jsx22("div", { className: `iv-chat-msg iv-chat-msg--${message.role}`, children: /* @__PURE__ */ jsx22("div", { className: "iv-chat-msg__bubble", children: message.content }) }, message.id)) }),
    /* @__PURE__ */ jsxs8("form", { className: "iv-chat-surface__composer", onSubmit: handleSubmit, children: [
      /* @__PURE__ */ jsx22("div", { className: "iv-chat-surface__field", children: /* @__PURE__ */ jsx22(
        Input,
        {
          value,
          onChange: (event) => setValue(event.currentTarget.value),
          placeholder,
          disabled: busy,
          "aria-label": "Message"
        }
      ) }),
      composerAccessory,
      /* @__PURE__ */ jsx22(Button, { type: "submit", variant: "primary", disabled: busy || value.trim().length === 0, children: "Send" })
    ] })
  ] });
}

// src/components/ItemCard/ItemCard.tsx
import { jsx as jsx23, jsxs as jsxs9 } from "react/jsx-runtime";
function ItemCard({ title, description, status, badge, meta, onClick, className }) {
  const classes = ["iv-item-card", onClick ? "iv-item-card--clickable" : "", className].filter(Boolean).join(" ");
  const interactive = onClick ? {
    onClick,
    role: "button",
    tabIndex: 0,
    onKeyDown: (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    }
  } : {};
  return /* @__PURE__ */ jsxs9(Card, { variant: "raised", className: classes, ...interactive, children: [
    /* @__PURE__ */ jsxs9("div", { className: "iv-item-card__head", children: [
      status && /* @__PURE__ */ jsx23(StatusDot, { color: status }),
      /* @__PURE__ */ jsx23(Text, { variant: "label", className: "iv-item-card__title", children: title }),
      badge && /* @__PURE__ */ jsx23(Badge, { variant: badge.variant, children: badge.label })
    ] }),
    description && /* @__PURE__ */ jsx23(Text, { variant: "body", className: "iv-item-card__desc", children: description }),
    meta && /* @__PURE__ */ jsx23(Text, { variant: "caption", className: "iv-item-card__meta", children: meta })
  ] });
}

// src/components/Tooltip/Tooltip.tsx
import {
  cloneElement,
  isValidElement,
  useCallback as useCallback4,
  useEffect as useEffect6,
  useId as useId4,
  useRef as useRef6,
  useState as useState7
} from "react";
import { createPortal as createPortal5 } from "react-dom";
import { jsx as jsx24, jsxs as jsxs10 } from "react/jsx-runtime";
var VIEWPORT_MARGIN3 = 8;
var TRIGGER_GAP3 = 8;
function computePosition(triggerRect, bubbleWidth, bubbleHeight, placement) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const needed = bubbleHeight + TRIGGER_GAP3 + VIEWPORT_MARGIN3;
  const spaceAbove = triggerRect.top;
  const spaceBelow = vh - triggerRect.bottom;
  let resolved = placement;
  if (placement === "top" && spaceAbove < needed && spaceBelow >= needed) {
    resolved = "bottom";
  } else if (placement === "bottom" && spaceBelow < needed && spaceAbove >= needed) {
    resolved = "top";
  }
  let top = resolved === "top" ? triggerRect.top - TRIGGER_GAP3 - bubbleHeight : triggerRect.bottom + TRIGGER_GAP3;
  top = Math.max(VIEWPORT_MARGIN3, Math.min(top, vh - bubbleHeight - VIEWPORT_MARGIN3));
  let left = triggerRect.left + triggerRect.width / 2 - bubbleWidth / 2;
  left = Math.max(VIEWPORT_MARGIN3, Math.min(left, vw - bubbleWidth - VIEWPORT_MARGIN3));
  return { top, left, placement: resolved };
}
function isFocusVisible(el) {
  try {
    return el.matches(":focus-visible");
  } catch {
    return true;
  }
}
function Tooltip({ label, children, placement = "top", id, className }) {
  const [open, setOpen] = useState7(false);
  const [coords, setCoords] = useState7(null);
  const wrapperRef = useRef6(null);
  const bubbleRef = useRef6(null);
  const generatedId = useId4();
  const tooltipId = id ?? generatedId;
  const show = useCallback4(() => setOpen(true), []);
  const hide = useCallback4(() => setOpen(false), []);
  const reposition = useCallback4(() => {
    const trigger2 = wrapperRef.current;
    const bubble = bubbleRef.current;
    if (!trigger2 || !bubble) return;
    const triggerRect = trigger2.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();
    setCoords(computePosition(triggerRect, bubbleRect.width, bubbleRect.height, placement));
  }, [placement]);
  useEffect6(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, reposition]);
  const onFocus = useCallback4(
    (event) => {
      if (isFocusVisible(event.currentTarget)) setOpen(true);
      children.props.onFocus?.(event);
    },
    [children]
  );
  const onBlur = useCallback4(
    (event) => {
      setOpen(false);
      children.props.onBlur?.(event);
    },
    [children]
  );
  const onKeyDown = useCallback4(
    (event) => {
      if (event.key === "Escape" && open) {
        event.preventDefault();
        setOpen(false);
      }
      children.props.onKeyDown?.(
        event
      );
    },
    [children, open]
  );
  if (!isValidElement(children)) {
    throw new Error("Tooltip expects a single React element child to act as the trigger.");
  }
  const describedBy = [
    children.props["aria-describedby"],
    tooltipId
  ].filter(Boolean).join(" ");
  const trigger = cloneElement(children, {
    "aria-describedby": describedBy,
    onFocus,
    onBlur,
    onKeyDown
  });
  const wrapperClasses = ["iv-tooltip", className].filter(Boolean).join(" ");
  const bubbleStyle = coords ? { top: coords.top, left: coords.left } : { top: 0, left: 0 };
  const resolvedPlacement = coords?.placement ?? placement;
  return /* @__PURE__ */ jsxs10("span", { ref: wrapperRef, className: wrapperClasses, onMouseEnter: show, onMouseLeave: hide, children: [
    trigger,
    open && typeof document !== "undefined" && createPortal5(
      /* @__PURE__ */ jsx24(
        "span",
        {
          ref: bubbleRef,
          role: "tooltip",
          id: tooltipId,
          className: `iv-tooltip__bubble iv-tooltip__bubble--${resolvedPlacement}`,
          "data-open": coords ? "true" : void 0,
          style: bubbleStyle,
          children: label
        }
      ),
      document.body
    )
  ] });
}

// src/components/Heatmap/Heatmap.tsx
import { useMemo as useMemo2 } from "react";

// src/components/Heatmap/heatmapGrid.ts
var DEFAULT_THRESHOLDS = [1, 3, 6, 10];
function levelFor(count, thresholds = DEFAULT_THRESHOLDS) {
  let level = 0;
  for (let i = 0; i < thresholds.length; i += 1) {
    if (count >= thresholds[i]) level = i + 1;
  }
  return level;
}
var MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
var DAY_MS = 864e5;
function toUtc(date) {
  const [y, m, d] = date.split("-").map(Number);
  return Date.UTC(y, (m ?? 1) - 1, d ?? 1);
}
function toIso(utc) {
  const d = new Date(utc);
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${d.getUTCFullYear()}-${mm}-${dd}`;
}
function buildHeatmapGrid(data, weeks, endDate, thresholds = DEFAULT_THRESHOLDS) {
  const end = toUtc(endDate);
  const endDow = new Date(end).getUTCDay();
  const start = end - endDow * DAY_MS - (weeks - 1) * 7 * DAY_MS;
  const counts = /* @__PURE__ */ new Map();
  for (const { date, count } of data) {
    const t = toUtc(date);
    if (t < start || t > end) continue;
    counts.set(date, (counts.get(date) ?? 0) + count);
  }
  const grid = [];
  const monthLabels = [];
  let total = 0;
  let lastMonth = -1;
  for (let w = 0; w < weeks; w += 1) {
    const column = [];
    const columnStart = start + w * 7 * DAY_MS;
    const month = new Date(columnStart).getUTCMonth();
    if (month !== lastMonth) {
      if (lastMonth !== -1) monthLabels.push({ weekIndex: w, label: MONTHS[month] });
      lastMonth = month;
    }
    for (let d = 0; d < 7; d += 1) {
      const t = columnStart + d * DAY_MS;
      if (t > end) {
        column.push(null);
        continue;
      }
      const date = toIso(t);
      const count = counts.get(date) ?? 0;
      total += count;
      column.push({ date, count, level: levelFor(count, thresholds) });
    }
    grid.push(column);
  }
  return { weeks: grid, monthLabels, total };
}

// src/components/Heatmap/Heatmap.tsx
import { jsx as jsx25, jsxs as jsxs11 } from "react/jsx-runtime";
var DAY_ROWS = ["", "Mon", "", "Wed", "", "Fri", ""];
function Heatmap({
  data,
  weeks = 26,
  endDate,
  thresholds = DEFAULT_THRESHOLDS,
  unit = "items",
  label,
  cellSize = 11,
  legend = true,
  onDayClick,
  className
}) {
  const grid = useMemo2(
    () => buildHeatmapGrid(data, weeks, endDate, thresholds),
    [data, weeks, endDate, thresholds]
  );
  const summary = label ?? `${grid.total} ${unit} in the last ${weeks} weeks`;
  const interactive = Boolean(onDayClick);
  const classes = ["iv-heatmap", interactive && "iv-heatmap--interactive", className].filter(Boolean).join(" ");
  const sizeStyle = { "--iv-heatmap-cell": `${cellSize}px` };
  return (
    // Non-interactive: one flat image with a summary. Interactive: the cells are
    // real buttons, so the figure must NOT be role="img" (that would hide them
    // from the accessibility tree) — the grid group carries the summary instead.
    /* @__PURE__ */ jsxs11(
      "figure",
      {
        className: classes,
        style: sizeStyle,
        role: interactive ? void 0 : "img",
        "aria-label": interactive ? void 0 : summary,
        children: [
          /* @__PURE__ */ jsx25("div", { className: "iv-heatmap__months", "aria-hidden": true, children: grid.monthLabels.map((m) => /* @__PURE__ */ jsx25(
            "span",
            {
              className: "iv-heatmap__month",
              style: { gridColumnStart: m.weekIndex + 1 },
              children: m.label
            },
            `${m.weekIndex}-${m.label}`
          )) }),
          /* @__PURE__ */ jsxs11("div", { className: "iv-heatmap__body", children: [
            /* @__PURE__ */ jsx25("div", { className: "iv-heatmap__days", "aria-hidden": true, children: DAY_ROWS.map((d, i) => /* @__PURE__ */ jsx25("span", { className: "iv-heatmap__day", children: d }, i)) }),
            /* @__PURE__ */ jsx25(
              "div",
              {
                className: "iv-heatmap__grid",
                "aria-hidden": interactive ? void 0 : true,
                role: interactive ? "group" : void 0,
                "aria-label": interactive ? summary : void 0,
                children: grid.weeks.map((week, w) => /* @__PURE__ */ jsx25("div", { className: "iv-heatmap__week", children: week.map((cell, d) => {
                  if (!cell) {
                    return /* @__PURE__ */ jsx25("span", { className: "iv-heatmap__cell iv-heatmap__cell--pad" }, d);
                  }
                  const cellLabel = `${cell.count} ${unit} \xB7 ${cell.date}`;
                  return interactive ? /* @__PURE__ */ jsx25(
                    "button",
                    {
                      type: "button",
                      className: "iv-heatmap__cell",
                      "data-level": cell.level,
                      title: cellLabel,
                      "aria-label": cellLabel,
                      onClick: () => onDayClick?.(cell)
                    },
                    d
                  ) : /* @__PURE__ */ jsx25(
                    "span",
                    {
                      className: "iv-heatmap__cell",
                      "data-level": cell.level,
                      title: cellLabel
                    },
                    d
                  );
                }) }, w))
              }
            )
          ] }),
          legend && /* @__PURE__ */ jsxs11("figcaption", { className: "iv-heatmap__legend", "aria-hidden": true, children: [
            /* @__PURE__ */ jsx25("span", { className: "iv-heatmap__legend-text", children: "Less" }),
            [0, 1, 2, 3, 4].map((level) => /* @__PURE__ */ jsx25("span", { className: "iv-heatmap__cell", "data-level": level }, level)),
            /* @__PURE__ */ jsx25("span", { className: "iv-heatmap__legend-text", children: "More" })
          ] })
        ]
      }
    )
  );
}

// src/components/Constellation/Constellation.tsx
import { lazy, Suspense, useCallback as useCallback5, useEffect as useEffect7, useMemo as useMemo3, useRef as useRef7, useState as useState8 } from "react";

// src/components/Constellation/grid.ts
var GRID_THRESHOLD = 400;
var REPULSION_CUTOFF = 140;
function forNearbyPairs(xs, ys, count, maxDist, visit, threshold = GRID_THRESHOLD) {
  if (count < 2) return;
  if (count <= threshold) {
    for (let i = 0; i < count; i++) {
      for (let j = i + 1; j < count; j++) visit(i, j);
    }
    return;
  }
  const cell = Math.max(1, maxDist);
  const buckets = /* @__PURE__ */ new Map();
  let minX = Infinity;
  let minY = Infinity;
  for (let i = 0; i < count; i++) {
    if (xs[i] < minX) minX = xs[i];
    if (ys[i] < minY) minY = ys[i];
  }
  const KEY_SPAN = 1 << 16;
  const keyOf = (cx, cy) => cx * KEY_SPAN + cy;
  const cellOf = (i) => {
    const cx = Math.floor((xs[i] - minX) / cell);
    const cy = Math.floor((ys[i] - minY) / cell);
    return { cx, cy };
  };
  for (let i = 0; i < count; i++) {
    const { cx, cy } = cellOf(i);
    const key = keyOf(cx, cy);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(i);
    else buckets.set(key, [i]);
  }
  const FORWARD = [
    [1, 0],
    // E
    [-1, 1],
    // SW
    [0, 1],
    // S
    [1, 1]
    // SE
  ];
  for (const [key, members] of buckets) {
    for (let a = 0; a < members.length; a++) {
      for (let b = a + 1; b < members.length; b++) visit(members[a], members[b]);
    }
    const cx = Math.floor(key / KEY_SPAN);
    const cy = key - cx * KEY_SPAN;
    for (const [dx, dy] of FORWARD) {
      const other = buckets.get(keyOf(cx + dx, cy + dy));
      if (!other) continue;
      for (const a of members) {
        for (const b of other) visit(a, b);
      }
    }
  }
}

// src/components/Constellation/graph.ts
var DEFAULT_MAX_HOP = 4;
var SKY_MARGIN = 30;
function angleDelta(from, to) {
  let d = to - from;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return d;
}
function hopDistances(nodes, edges, rootId, maxHop = DEFAULT_MAX_HOP) {
  const adj = /* @__PURE__ */ new Map();
  for (const e of edges) {
    if (!adj.has(e.source)) adj.set(e.source, []);
    if (!adj.has(e.target)) adj.set(e.target, []);
    adj.get(e.source).push(e.target);
    adj.get(e.target).push(e.source);
  }
  const hops = /* @__PURE__ */ new Map();
  const root = rootId && nodes.find((n) => n.id === rootId) || nodes.find((n) => n.focus);
  if (!root) return hops;
  hops.set(root.id, 0);
  let frontier = [root.id];
  for (let d = 1; d <= maxHop && frontier.length; d++) {
    const next = [];
    for (const id of frontier) {
      for (const nb of adj.get(id) ?? []) {
        if (!hops.has(nb)) {
          hops.set(nb, d);
          next.push(nb);
        }
      }
    }
    frontier = next;
  }
  for (const n of nodes) if (!hops.has(n.id)) hops.set(n.id, maxHop);
  return hops;
}
function filterByTime(graph, atTime) {
  if (atTime === null || atTime === void 0) return graph;
  const nodes = graph.nodes.filter((n) => n.keepAlways || n.t === void 0 || n.t <= atTime);
  const keep = new Set(nodes.map((n) => n.id));
  const edges = graph.edges.filter(
    (e) => keep.has(e.source) && keep.has(e.target) && (e.t === void 0 || e.t <= atTime)
  );
  return { nodes, edges };
}
function filterByReach(graph, hops, reach, maxHop = DEFAULT_MAX_HOP) {
  if (reach >= maxHop) return graph;
  const keep = new Set(graph.nodes.filter((n) => (hops.get(n.id) ?? maxHop) <= reach).map((n) => n.id));
  return {
    nodes: graph.nodes.filter((n) => keep.has(n.id)),
    edges: graph.edges.filter((e) => keep.has(e.source) && keep.has(e.target))
  };
}
function computeRingRadii(hops, w, h, reach, maxHop = DEFAULT_MAX_HOP) {
  const counts = /* @__PURE__ */ new Map();
  for (const hp of hops.values()) counts.set(hp, (counts.get(hp) ?? 0) + 1);
  const total = hops.size || 1;
  const atScale = total > GRID_THRESHOLD;
  const ARC_PER_NODE = atScale ? Math.max(10, 62 * GRID_THRESHOLD / total) : 62;
  const MIN_GAP = 68;
  const radii = [0];
  for (let hp = 1; hp <= maxHop; hp++) {
    const needed = (counts.get(hp) ?? 0) * ARC_PER_NODE / (2 * Math.PI);
    radii[hp] = Math.max(radii[hp - 1] + MIN_GAP, needed);
  }
  const maxR = Math.min(w, h) / 2 - SKY_MARGIN;
  const outermost = Math.min(reach, maxHop);
  const fit = radii[outermost] > 0 ? maxR / radii[outermost] : 1;
  const k = atScale ? Math.max(1, fit) : fit;
  return radii.map((r) => r * k);
}
function sectorsByGroup(nodes, centerId) {
  const counts = /* @__PURE__ */ new Map();
  for (const n of nodes) {
    if (n.id === centerId) continue;
    counts.set(n.group, (counts.get(n.group) ?? 0) + 1);
  }
  const names = [...counts.keys()].sort();
  const total = [...counts.values()].reduce((a, b) => a + b, 0) || 1;
  const out = /* @__PURE__ */ new Map();
  let cursor = -Math.PI / 2;
  for (const name of names) {
    const span = 2 * Math.PI * (counts.get(name) ?? 0) / total;
    out.set(name, { start: cursor, end: cursor + span, mid: cursor + span / 2 });
    cursor += span;
  }
  return out;
}
function fitCamera(world, w, h) {
  const k = Math.min(w / world.w, h / world.h, 1);
  return { k, tx: (w - world.w * k) / 2, ty: (h - world.h * k) / 2 };
}
function fitCameraZoomed(world, w, h, zoom = 1) {
  const fit = fitCamera(world, w, h);
  if (zoom === 1) return fit;
  const k = fit.k * zoom;
  return { k, tx: w / 2 - world.w / 2 * k, ty: h / 2 - world.h / 2 * k };
}
function projectCenterOffset(cam, world, tiltY, dx, dy) {
  const cx = world.w / 2;
  const cy = world.h / 2;
  const wy = cy + dy * tiltY;
  return { x: (cx + dx) * cam.k + cam.tx, y: wy * cam.k + cam.ty };
}
function zoomAround(cam, sx, sy, factor, fitK, maxK) {
  const k = Math.min(maxK, Math.max(fitK * 0.5, cam.k * factor));
  const wx = (sx - cam.tx) / cam.k;
  const wy = (sy - cam.ty) / cam.k;
  return { k, tx: sx - wx * k, ty: sy - wy * k };
}
function screenToWorld(cam, sx, sy) {
  return { x: (sx - cam.tx) / cam.k, y: (sy - cam.ty) / cam.k };
}
function tiltFactor(mode) {
  return mode === "3d" ? 0.58 : 1;
}

// src/components/Constellation/Constellation.tsx
import { jsx as jsx26, jsxs as jsxs12 } from "react/jsx-runtime";
var GalaxyLayer = lazy(() => import("./GalaxyLayer-GBNVNQWQ.js"));
var MAX_H = 760;
var MIN_H = 260;
var MAX_LABELS = 12;
var ALPHA_DECAY = 0.985;
var ALPHA_MIN = 0.02;
var RING_PULL = 0.12;
var RADIAL_LEAK = 0.15;
var SPRING_REST = 90;
var ANGLE_PULL = 0.05;
var SECTOR_PULL = 0.012;
var GALAXY_K = 0.45;
var RICH_K = 0.75;
var MAX_K = 3;
var DEFAULT_THEME = {
  ring: "#c9a96e",
  // --ae-brand-gold
  star: "#e8e2d9",
  // --ae-brand-parchment
  starHalo: "#7a7268",
  // --ae-brand-ash
  focusCore: "#e0564a",
  // --ae-color-crimson-400
  focusHalo: "#f48a7f",
  // --ae-color-crimson-300
  label: "#e8e2d9",
  sector: "#7a7268",
  muted: "#7a7268"
};
function baseRadius(node, degree, isFocus) {
  if (typeof node.weight === "number") return node.weight;
  if (isFocus) return 26;
  return 9 + Math.min(13, Math.sqrt(degree) * 5);
}
function Constellation(props) {
  const {
    nodes: rawNodes,
    edges: rawEdges,
    focusId: focusIdProp = null,
    egoId = null,
    maxHop = DEFAULT_MAX_HOP,
    mode = "2d",
    timeExtent: timeExtentProp,
    timeControl = true,
    getNodeColor,
    getNodeLabel,
    getGroupColor,
    reducedMotion = false,
    className,
    onNodeClick,
    onNodePin,
    onBackgroundClick,
    onResetFocus,
    onViewStats,
    children,
    stagePositions,
    lockCamera = false,
    initialZoom = 1,
    labelIds,
    hopRings = true,
    sectorRays = true,
    overlay
  } = props;
  const reach = props.reach ?? maxHop;
  const theme = { ...DEFAULT_THEME, ...props.theme };
  const labelOf = useCallback5((n) => getNodeLabel?.(n) ?? n.label, [getNodeLabel]);
  const groupColorOf = useCallback5((g) => getGroupColor?.(g), [getGroupColor]);
  const graph = useMemo3(() => ({ nodes: rawNodes, edges: rawEdges }), [rawNodes, rawEdges]);
  const [asOfInternal, setAsOfInternal] = useState8(null);
  const controlledTime = props.atTime !== void 0;
  const asOf = controlledTime ? props.atTime ?? null : asOfInternal;
  const setAsOf = useCallback5(
    (t) => {
      if (!controlledTime) setAsOfInternal(t);
      props.onTimeChange?.(t);
    },
    [controlledTime, props]
  );
  const asOfRef = useRef7(asOf);
  asOfRef.current = asOf;
  const [playing, setPlaying] = useState8(false);
  const timeSpan = useMemo3(() => {
    if (timeExtentProp) return timeExtentProp;
    const stamps = graph.nodes.map((n) => n.t).filter((t) => typeof t === "number");
    if (stamps.length < 2) return null;
    return { min: Math.min(...stamps), max: Date.now() };
  }, [graph, timeExtentProp]);
  const timed = useMemo3(() => {
    const flagged = {
      nodes: graph.nodes.map((n) => ({ ...n, keepAlways: n.id === egoId })),
      edges: graph.edges.map((e) => ({ ...e, source: e.from, target: e.to, t: e.t }))
    };
    const r = filterByTime(flagged, asOf);
    return r;
  }, [graph, asOf, egoId]);
  useEffect7(() => {
    if (!playing || !timeSpan) return;
    const start = asOfRef.current ?? timeSpan.min;
    const step = (timeSpan.max - timeSpan.min) / 60;
    const timer = setInterval(() => {
      const next = (asOfRef.current ?? start) + step;
      if (next >= timeSpan.max) {
        setPlaying(false);
        setAsOf(null);
      } else {
        setAsOf(next);
      }
    }, 100);
    return () => clearInterval(timer);
  }, [playing, timeSpan]);
  const centerId = useMemo3(() => {
    if (focusIdProp && timed.nodes.some((n) => n.id === focusIdProp)) return focusIdProp;
    if (egoId && timed.nodes.some((n) => n.id === egoId)) return egoId;
    return timed.nodes[0]?.id ?? null;
  }, [timed, focusIdProp, egoId]);
  const fullHops = useMemo3(
    () => hopDistances(timed.nodes.map((n) => ({ id: n.id, focus: n.id === centerId })), timed.edges, centerId, maxHop),
    [timed, centerId, maxHop]
  );
  const scoped = useMemo3(() => filterByReach(timed, fullHops, reach, maxHop), [timed, fullHops, reach, maxHop]);
  const hops = useMemo3(
    () => hopDistances(scoped.nodes.map((n) => ({ id: n.id, focus: n.id === centerId })), scoped.edges, centerId, maxHop),
    [scoped, centerId, maxHop]
  );
  useEffect7(() => {
    onViewStats?.({
      visible: scoped.nodes.length,
      hidden: timed.nodes.length - scoped.nodes.length,
      total: timed.nodes.length,
      relations: scoped.edges.length
    });
  }, [scoped, timed, onViewStats]);
  const degree = useMemo3(() => {
    const d = /* @__PURE__ */ new Map();
    for (const e of scoped.edges) {
      d.set(e.source, (d.get(e.source) ?? 0) + 1);
      d.set(e.target, (d.get(e.target) ?? 0) + 1);
    }
    return d;
  }, [scoped]);
  const neighbours = useMemo3(() => {
    const adj = /* @__PURE__ */ new Map();
    for (const e of scoped.edges) {
      if (!adj.has(e.source)) adj.set(e.source, []);
      if (!adj.has(e.target)) adj.set(e.target, []);
      adj.get(e.source).push(e.target);
      adj.get(e.target).push(e.source);
    }
    return adj;
  }, [scoped]);
  const sectors = useMemo3(
    () => sectorsByGroup(scoped.nodes.map((n) => ({ id: n.id, group: n.group ?? "other" })), centerId),
    [scoped, centerId]
  );
  const centered = useMemo3(() => scoped.nodes.find((n) => n.id === centerId) ?? null, [scoped, centerId]);
  const wrapRef = useRef7(null);
  const svgRef = useRef7(null);
  const [w, setW] = useState8(900);
  const [H, setH] = useState8(MAX_H);
  const [tick, setTick] = useState8(0);
  const [hovered, setHovered] = useState8(null);
  const hoverScanRef = useRef7(0);
  useEffect7(() => {
    const el = wrapRef.current;
    if (!el) return;
    const fit = () => {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (cw) setW(Math.max(320, Math.floor(cw)));
      setH(Math.max(MIN_H, Math.min(MAX_H, Math.floor(ch || MAX_H))));
    };
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    fit();
    window.addEventListener("resize", fit);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, []);
  const ringRadii = useMemo3(() => computeRingRadii(hops, w, H, reach, maxHop), [hops, w, H, reach, maxHop]);
  const ringRadiiRef = useRef7(ringRadii);
  ringRadiiRef.current = ringRadii;
  const ringR = useCallback5((hop) => ringRadiiRef.current[Math.min(hop, maxHop)] ?? 0, [maxHop]);
  const outerHop = useMemo3(() => {
    let m = 1;
    for (const hp of hops.values()) if (hp > m) m = hp;
    return Math.min(m, maxHop);
  }, [hops, maxHop]);
  const world = useMemo3(() => {
    const outer = ringRadii[outerHop] ?? 0;
    const side = outer * 2 + SKY_MARGIN * 2;
    return { w: Math.max(w, side), h: Math.max(H, side) };
  }, [ringRadii, outerHop, w, H]);
  const worldRef = useRef7(world);
  worldRef.current = world;
  const prevWorldRef = useRef7(0);
  const [cam, setCam] = useState8({ k: 1, tx: 0, ty: 0 });
  const camRef = useRef7(cam);
  camRef.current = cam;
  const fitKRef = useRef7(1);
  useEffect7(() => {
    fitKRef.current = fitCamera(world, w, H).k;
    setCam(fitCameraZoomed(world, w, H, initialZoom));
  }, [world.w, world.h, w, H, initialZoom]);
  const lockRef = useRef7(lockCamera);
  lockRef.current = lockCamera;
  useEffect7(() => {
    if (lockCamera) return;
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (ev) => {
      ev.preventDefault();
      const rect = el.getBoundingClientRect();
      const sx = ev.clientX - rect.left;
      const sy = ev.clientY - rect.top;
      setCam((c) => zoomAround(c, sx, sy, Math.exp(-ev.deltaY * 16e-4), fitKRef.current, MAX_K));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [lockCamera]);
  const tiltY = tiltFactor(mode);
  const tiltYRef = useRef7(tiltY);
  tiltYRef.current = tiltY;
  const richOpacity = lockCamera ? 1 : Math.max(0, Math.min(1, (cam.k - GALAXY_K) / (RICH_K - GALAXY_K)));
  const galaxyOpacity = 1 - richOpacity;
  const rScale = useMemo3(() => {
    const total = scoped.nodes.length;
    return total <= GRID_THRESHOLD ? 1 : Math.max(0.25, Math.sqrt(GRID_THRESHOLD / total));
  }, [scoped]);
  const resolveOverride = useCallback5(
    (n) => getNodeColor?.(n) ?? n.color,
    [getNodeColor]
  );
  const galaxyVisuals = useMemo3(() => {
    const m = /* @__PURE__ */ new Map();
    for (const n of scoped.nodes) {
      const isEgo = n.id === egoId;
      const override = resolveOverride(n);
      m.set(n.id, {
        size: baseRadius(n, degree.get(n.id) ?? 0, n.id === centerId) * rScale,
        bright: isEgo ? 1 : n.brightness ?? 0.5,
        color: override ?? (isEgo ? theme.focusHalo : n.group ? groupColorOf(n.group) ?? theme.star : theme.star)
      });
    }
    return m;
  }, [scoped, degree, rScale, egoId, centerId, resolveOverride, groupColorOf, theme.focusHalo, theme.star]);
  const galaxyEverRef = useRef7(false);
  if (galaxyOpacity > 0.02) galaxyEverRef.current = true;
  const hopsRef = useRef7(hops);
  const centerIdRef = useRef7(null);
  const neighboursRef = useRef7(neighbours);
  const sectorsRef = useRef7(sectors);
  const degreeRef = useRef7(degree);
  const nodesRef = useRef7([]);
  const edgesRef = useRef7([]);
  const dragRef = useRef7(null);
  const pinsRef = useRef7(/* @__PURE__ */ new Map());
  const panRef = useRef7(null);
  const pinchRef = useRef7(null);
  const rafRef = useRef7(void 0);
  const alphaRef = useRef7(1);
  const runningRef = useRef7(false);
  const stepRef = useRef7(() => {
  });
  const reducedRef = useRef7(reducedMotion);
  reducedRef.current = reducedMotion;
  const onNodePinRef = useRef7(onNodePin);
  onNodePinRef.current = onNodePin;
  const onNodeClickRef = useRef7(onNodeClick);
  onNodeClickRef.current = onNodeClick;
  const onBackgroundClickRef = useRef7(onBackgroundClick);
  onBackgroundClickRef.current = onBackgroundClick;
  const reheat = useCallback5((a = 1) => {
    alphaRef.current = Math.max(alphaRef.current, a);
    if (!runningRef.current) {
      runningRef.current = true;
      rafRef.current = requestAnimationFrame(() => stepRef.current());
    }
  }, []);
  useEffect7(() => {
    const cx2 = world.w / 2;
    const cy2 = world.h / 2;
    hopsRef.current = hops;
    centerIdRef.current = centerId;
    neighboursRef.current = neighbours;
    sectorsRef.current = sectors;
    degreeRef.current = degree;
    const lastW = prevWorldRef.current;
    const worldJumped = lastW > 0 && (world.w / lastW > 1.3 || world.w / lastW < 0.77);
    prevWorldRef.current = world.w;
    const prev = worldJumped ? /* @__PURE__ */ new Map() : new Map(nodesRef.current.map((n) => [n.id, n]));
    const seedAngle = /* @__PURE__ */ new Map();
    const byHop = [...scoped.nodes].sort((a, b) => (hops.get(a.id) ?? maxHop) - (hops.get(b.id) ?? maxHop));
    const fanCount = /* @__PURE__ */ new Map();
    byHop.forEach((node, i) => {
      if (node.id === centerId) {
        seedAngle.set(node.id, 0);
        return;
      }
      const hop = hops.get(node.id) ?? maxHop;
      const parent = (neighbours.get(node.id) ?? []).find(
        (nb) => (hops.get(nb) ?? maxHop) === hop - 1 && seedAngle.has(nb)
      );
      if (parent !== void 0 && hop > 1) {
        const fan = fanCount.get(parent) ?? 0;
        fanCount.set(parent, fan + 1);
        const offset = Math.ceil((fan + 1) / 2) * 0.22 * (fan % 2 === 0 ? 1 : -1);
        seedAngle.set(node.id, seedAngle.get(parent) + offset);
        return;
      }
      const sector = sectors.get(node.group ?? "other");
      seedAngle.set(
        node.id,
        sector ? sector.start + (i % 7 + 1) * ((sector.end - sector.start) / 8) : i / Math.max(1, scoped.nodes.length) * Math.PI * 2
      );
    });
    nodesRef.current = scoped.nodes.map((node, i) => {
      const p = prev.get(node.id);
      const angle = seedAngle.get(node.id) ?? i / Math.max(1, scoped.nodes.length) * Math.PI * 2;
      const seedR = ringR(hops.get(node.id) ?? maxHop);
      const seedX = cx2 + Math.cos(angle) * seedR;
      const seedY = cy2 + Math.sin(angle) * seedR;
      const pin = node.id === centerId ? null : pinsRef.current.get(node.id) ?? stagePositions?.[node.id] ?? node.pinnedPos ?? null;
      return {
        id: node.id,
        x: node.id === centerId ? cx2 : pin ? cx2 + pin.x : p?.x ?? seedX,
        y: node.id === centerId ? cy2 : pin ? cy2 + pin.y : p?.y ?? seedY,
        vx: 0,
        vy: 0,
        r: baseRadius(node, degree.get(node.id) ?? 0, node.id === centerId) * rScale,
        node,
        self: node.id === egoId,
        pin
      };
    });
    edgesRef.current = scoped.edges.map((e) => ({ source: e.source, target: e.target, kind: e.kind, label: e.label }));
    reheat(1);
  }, [scoped, w, H, world, degree, hops, neighbours, sectors, ringR, reheat, centerId, rScale, egoId, maxHop, stagePositions]);
  useEffect7(() => {
    function step() {
      const nodes2 = nodesRef.current;
      const edges = edgesRef.current;
      const byId2 = new Map(nodes2.map((n) => [n.id, n]));
      const index = new Map(nodes2.map((n, i) => [n.id, i]));
      const count = nodes2.length;
      const xs = new Float64Array(count);
      const ys = new Float64Array(count);
      for (let i = 0; i < count; i++) {
        xs[i] = nodes2[i].x;
        ys[i] = nodes2[i].y;
      }
      const fxA = new Float64Array(count);
      const fyA = new Float64Array(count);
      const gridMode = count > GRID_THRESHOLD;
      const cutoff2 = REPULSION_CUTOFF * REPULSION_CUTOFF;
      forNearbyPairs(xs, ys, count, REPULSION_CUTOFF, (i, j) => {
        const dx = xs[i] - xs[j];
        const dy = ys[i] - ys[j];
        const d2 = dx * dx + dy * dy + 0.01;
        if (gridMode && d2 > cutoff2) return;
        const d = Math.sqrt(d2);
        const rep = 7200 / d2;
        const ux = dx / d;
        const uy = dy / d;
        fxA[i] += ux * rep;
        fyA[i] += uy * rep;
        fxA[j] -= ux * rep;
        fyA[j] -= uy * rep;
      });
      for (const e of edges) {
        const ia = index.get(e.source);
        const ib = index.get(e.target);
        if (ia === void 0 || ib === void 0) continue;
        const dx = xs[ib] - xs[ia];
        const dy = ys[ib] - ys[ia];
        const d = Math.sqrt(dx * dx + dy * dy) + 0.01;
        const f = (d - SPRING_REST) * 0.014;
        const ux = dx / d;
        const uy = dy / d;
        fxA[ia] += ux * f;
        fyA[ia] += uy * f;
        fxA[ib] -= ux * f;
        fyA[ib] -= uy * f;
      }
      const cx2 = worldRef.current.w / 2;
      const cy2 = worldRef.current.h / 2;
      for (let ni = 0; ni < count; ni++) {
        const n = nodes2[ni];
        if (n.id === centerIdRef.current) {
          n.x = cx2;
          n.y = cy2;
          n.vx = 0;
          n.vy = 0;
          continue;
        }
        if (dragRef.current?.id === n.id) {
          n.vx = 0;
          n.vy = 0;
          continue;
        }
        if (n.pin) {
          n.x = cx2 + n.pin.x;
          n.y = cy2 + n.pin.y;
          n.vx = 0;
          n.vy = 0;
          continue;
        }
        const alpha = alphaRef.current;
        const targetR = ringR(hopsRef.current.get(n.id) ?? maxHop);
        const ddx = n.x - cx2;
        const ddy = n.y - cy2;
        const dist = Math.hypot(ddx, ddy) || 0.01;
        const ux = ddx / dist;
        const uy = ddy / dist;
        const gx = fxA[ni];
        const gy = fyA[ni];
        const fRadial = gx * ux + gy * uy;
        let fTangent = gx * -uy + gy * ux;
        const thetaN = Math.atan2(ddy, ddx);
        const adjacent = neighboursRef.current.get(n.id) ?? [];
        if (adjacent.length) {
          let sx = 0;
          let sy = 0;
          for (const id of adjacent) {
            const other = byId2.get(id);
            if (!other || other.id === centerIdRef.current) continue;
            const a = Math.atan2(other.y - cy2, other.x - cx2);
            sx += Math.cos(a);
            sy += Math.sin(a);
          }
          if (sx !== 0 || sy !== 0) {
            const want = Math.atan2(sy, sx);
            const weight = 1 / (degreeRef.current.get(n.id) ?? 1);
            fTangent += angleDelta(thetaN, want) * dist * ANGLE_PULL * weight;
          }
        }
        const sector = sectorsRef.current.get(n.node.group ?? "other");
        if (sector) fTangent += angleDelta(thetaN, sector.mid) * dist * SECTOR_PULL;
        const ringForce = (targetR - dist) * RING_PULL;
        const radial = ringForce + fRadial * RADIAL_LEAK;
        const ax = (ux * radial + -uy * fTangent) * alpha;
        const ay = (uy * radial + ux * fTangent) * alpha;
        n.vx = (n.vx + ax) * 0.8;
        n.vy = (n.vy + ay) * 0.8;
        n.x += n.vx;
        n.y += n.vy;
        n.x = Math.max(n.r + 8, Math.min(worldRef.current.w - n.r - 8, n.x));
        n.y = Math.max(n.r + 24, Math.min(worldRef.current.h - n.r - 16, n.y));
        const dcx = n.x - cx2;
        const dcy = n.y - cy2;
        const dc = Math.hypot(dcx, dcy);
        const minD = 26 + n.r + 16;
        if (dc < minD) {
          if (dc < 1e-3) {
            n.x = cx2 + minD;
            n.y = cy2;
          } else {
            n.x = cx2 + dcx / dc * minD;
            n.y = cy2 + dcy / dc * minD;
          }
        }
      }
      let maxNodeR = 0;
      for (const n of nodes2) if (n.r > maxNodeR) maxNodeR = n.r;
      for (let i = 0; i < count; i++) {
        xs[i] = nodes2[i].x;
        ys[i] = nodes2[i].y;
      }
      forNearbyPairs(xs, ys, count, 2 * maxNodeR + 6, (i, j) => {
        const a = nodes2[i];
        const b = nodes2[j];
        if (a.id === centerIdRef.current || b.id === centerIdRef.current) return;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const d = Math.hypot(dx, dy);
        const minimum = a.r + b.r + 6;
        if (d >= minimum || d < 0.01) return;
        const push = (minimum - d) / 2;
        const ux = dx / d;
        const uy = dy / d;
        if (dragRef.current?.id !== a.id && !a.pin) {
          a.x -= ux * push;
          a.y -= uy * push;
        }
        if (dragRef.current?.id !== b.id && !b.pin) {
          b.x += ux * push;
          b.y += uy * push;
        }
      });
      setTick((t) => (t + 1) % 1e6);
      if (dragRef.current) alphaRef.current = Math.max(alphaRef.current, 0.35);
      else if (reducedRef.current) alphaRef.current = 0;
      else alphaRef.current *= ALPHA_DECAY;
      if (alphaRef.current < ALPHA_MIN) {
        runningRef.current = false;
        return;
      }
      rafRef.current = requestAnimationFrame(step);
    }
    stepRef.current = step;
    reheat(1);
    return () => {
      runningRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [w, H, ringR, reheat, maxHop]);
  const screenToWorldTilted = useCallback5(
    (sx, sy) => {
      const c = camRef.current;
      const p = screenToWorld(c, sx, sy);
      const cy2 = worldRef.current.h / 2;
      const ty = tiltYRef.current;
      return { x: p.x, y: cy2 + (p.y - cy2) / ty };
    },
    []
  );
  useEffect7(() => {
    function onMove(ev) {
      const pinch = pinchRef.current;
      if (pinch) {
        if (ev.pointerId === pinch.id1) {
          pinch.x1 = ev.clientX;
          pinch.y1 = ev.clientY;
        } else if (ev.pointerId === pinch.id2) {
          pinch.x2 = ev.clientX;
          pinch.y2 = ev.clientY;
        } else return;
        const wrap2 = wrapRef.current;
        if (!wrap2) return;
        const rect2 = wrap2.getBoundingClientRect();
        const midX = (pinch.x1 + pinch.x2) / 2 - rect2.left;
        const midY = (pinch.y1 + pinch.y2) / 2 - rect2.top;
        const d2 = Math.hypot(pinch.x2 - pinch.x1, pinch.y2 - pinch.y1);
        const k = Math.min(MAX_K, Math.max(fitKRef.current * 0.5, pinch.k0 * (d2 / (pinch.d0 || 1))));
        setCam({ k, tx: midX - pinch.wx * k, ty: midY - pinch.wy * k });
        return;
      }
      const pan = panRef.current;
      if (pan) {
        if (ev.pointerId !== pan.pointerId) return;
        pan.lx = ev.clientX;
        pan.ly = ev.clientY;
        if (Math.hypot(ev.clientX - pan.sx, ev.clientY - pan.sy) > 3) pan.moved = true;
        if (lockRef.current) return;
        setCam((c) => ({ k: c.k, tx: pan.tx0 + (ev.clientX - pan.sx), ty: pan.ty0 + (ev.clientY - pan.sy) }));
        return;
      }
      const d = dragRef.current;
      const wrap = wrapRef.current;
      if (!d || !wrap) return;
      if (Math.hypot(ev.clientX - d.sx, ev.clientY - d.sy) > 3) d.moved = true;
      if (lockRef.current) return;
      const rect = wrap.getBoundingClientRect();
      const n = nodesRef.current.find((nn) => nn.id === d.id);
      if (n && n.id !== centerIdRef.current) {
        const p = screenToWorldTilted(ev.clientX - rect.left, ev.clientY - rect.top);
        n.x = p.x;
        n.y = p.y;
        n.vx = 0;
        n.vy = 0;
        reheat(0.35);
      }
    }
    function onUp(ev) {
      const pinch = pinchRef.current;
      if (pinch) {
        if (ev.pointerId === pinch.id1 || ev.pointerId === pinch.id2) pinchRef.current = null;
        return;
      }
      const pan = panRef.current;
      if (pan) {
        if (ev.pointerId !== pan.pointerId) return;
        if (!pan.moved) {
          if (pan.galaxy) {
            const wrap = wrapRef.current;
            if (wrap) {
              const rect = wrap.getBoundingClientRect();
              const p = screenToWorldTilted(ev.clientX - rect.left, ev.clientY - rect.top);
              let best;
              let bestD = 14 / camRef.current.k;
              for (const n of nodesRef.current) {
                const d2 = Math.hypot(n.x - p.x, n.y - p.y);
                if (d2 < bestD) {
                  bestD = d2;
                  best = n;
                }
              }
              if (best) onNodeClickRef.current?.(best.node);
              else onBackgroundClickRef.current?.();
            }
          } else {
            onBackgroundClickRef.current?.();
          }
        }
        panRef.current = null;
        return;
      }
      const d = dragRef.current;
      if (d) {
        const n = nodesRef.current.find((nn) => nn.id === d.id);
        if (n && !d.moved) {
          onNodeClickRef.current?.(n.node, d.anchor);
        } else if (n && n.id !== centerIdRef.current && !lockRef.current) {
          const pin = { x: n.x - worldRef.current.w / 2, y: n.y - worldRef.current.h / 2 };
          n.pin = pin;
          pinsRef.current.set(n.id, pin);
          onNodePinRef.current?.(n.id, pin);
        }
      }
      dragRef.current = null;
    }
    function onCancel() {
      panRef.current = null;
      pinchRef.current = null;
      dragRef.current = null;
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [reheat, screenToWorldTilted]);
  const nodes = nodesRef.current;
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const cx = world.w / 2;
  const cy = world.h / 2;
  const cullPad = 140;
  const visX1 = (0 - cam.tx) / cam.k - cullPad;
  const visY1 = (0 - cam.ty) / cam.k - cullPad;
  const visX2 = (w - cam.tx) / cam.k + cullPad;
  const visY2 = (H - cam.ty) / cam.k + cullPad;
  const cullOn = nodes.length > GRID_THRESHOLD;
  const drawNodes = cullOn ? nodes.filter((n) => n.x >= visX1 && n.x <= visX2 && n.y >= visY1 && n.y <= visY2) : nodes;
  const galaxyHubs = galaxyOpacity > 0.3 ? (() => {
    const picked = /* @__PURE__ */ new Map();
    for (const n of nodes) {
      if (n.id === centerId || n.id === hovered) picked.set(n.id, n);
    }
    const byDegree = [...nodes].sort((a, b) => (degree.get(b.id) ?? 0) - (degree.get(a.id) ?? 0));
    const boxes = [];
    const claim = (n) => {
      const sx = n.x * cam.k + cam.tx;
      const sy = n.y * cam.k + cam.ty;
      const half = labelOf(n.node).length * 6 / 2;
      const box = { x1: sx - half, y1: sy, x2: sx + half, y2: sy + 14 };
      if (boxes.some((b) => !(box.x2 < b.x1 || box.x1 > b.x2 || box.y2 < b.y1 || box.y1 > b.y2))) return false;
      boxes.push(box);
      return true;
    };
    for (const n of picked.values()) claim(n);
    for (const n of byDegree) {
      if (picked.size >= 12) break;
      if (picked.has(n.id)) continue;
      if (claim(n)) picked.set(n.id, n);
    }
    return [...picked.values()];
  })() : [];
  const focus = hovered ? /* @__PURE__ */ new Set([hovered, ...neighbours.get(hovered) ?? []]) : null;
  const hoveredNode = hovered ? byId.get(hovered) : void 0;
  const whitelist = labelIds ? new Set(labelIds) : null;
  const labelled = /* @__PURE__ */ new Set();
  const claimed = [];
  const labelShrink = Math.max(1, cam.k);
  const CHAR_W = 6.2 / labelShrink;
  const LABEL_H = 13 / labelShrink;
  const boxOf2 = (n) => {
    const halfW = labelOf(n.node).length * CHAR_W / 2;
    return { x1: n.x - halfW, y1: n.y + n.r + 4, x2: n.x + halfW, y2: n.y + n.r + 4 + LABEL_H };
  };
  if (whitelist) {
    for (const n of drawNodes) if (whitelist.has(n.id)) labelled.add(n.id);
  } else {
    for (const n of drawNodes) if (n.id === egoId) claimed.push(boxOf2(n));
    for (const n of [...drawNodes].filter((nn) => nn.id !== egoId).sort((a, b) => (degree.get(b.id) ?? 0) - (degree.get(a.id) ?? 0))) {
      if (labelled.size >= MAX_LABELS) break;
      const box = boxOf2(n);
      const hits = claimed.some((c) => !(box.x2 < c.x1 || box.x1 > c.x2 || box.y2 < c.y1 || box.y1 > c.y2));
      if (hits) continue;
      claimed.push(box);
      labelled.add(n.id);
    }
  }
  const showGalaxy = scoped.nodes.length > 0 && (galaxyOpacity > 0.02 || galaxyEverRef.current);
  const showRich = scoped.nodes.length > 0 && richOpacity > 0.04;
  const worldTransform = `translate(${cam.tx} ${cam.ty}) scale(${cam.k}) translate(0 ${cy}) scale(1 ${tiltY}) translate(0 ${-cy})`;
  return /* @__PURE__ */ jsxs12(
    "div",
    {
      ref: wrapRef,
      className: `iv-constellation${className ? ` ${className}` : ""}`,
      "data-mode": mode,
      onPointerDown: (ev) => {
        const pan = panRef.current;
        if (pan && !pinchRef.current && ev.pointerId !== pan.pointerId && !lockCamera) {
          const rect = ev.currentTarget.getBoundingClientRect();
          const c = camRef.current;
          const midX = (pan.lx + ev.clientX) / 2 - rect.left;
          const midY = (pan.ly + ev.clientY) / 2 - rect.top;
          pinchRef.current = {
            id1: pan.pointerId,
            id2: ev.pointerId,
            x1: pan.lx,
            y1: pan.ly,
            x2: ev.clientX,
            y2: ev.clientY,
            k0: c.k,
            d0: Math.hypot(ev.clientX - pan.lx, ev.clientY - pan.ly),
            wx: (midX - c.tx) / c.k,
            wy: (midY - c.ty) / c.k
          };
          panRef.current = null;
          dragRef.current = null;
          return;
        }
        if (dragRef.current || panRef.current || pinchRef.current) return;
        if (ev.target.closest(".iv-history, .iv-focus-crumb, .iv-hover-card, button, input, select"))
          return;
        panRef.current = {
          pointerId: ev.pointerId,
          sx: ev.clientX,
          sy: ev.clientY,
          lx: ev.clientX,
          ly: ev.clientY,
          tx0: camRef.current.tx,
          ty0: camRef.current.ty,
          k: camRef.current.k,
          moved: false,
          galaxy: galaxyOpacity > 0.5
        };
      },
      onPointerMove: (ev) => {
        if (richOpacity >= 0.15 || panRef.current || dragRef.current) return;
        const now = performance.now();
        if (now - hoverScanRef.current < 30) return;
        hoverScanRef.current = now;
        const rect = ev.currentTarget.getBoundingClientRect();
        const p = screenToWorldTilted(ev.clientX - rect.left, ev.clientY - rect.top);
        let bestId = null;
        let bestD = 14 / camRef.current.k;
        for (const n of nodesRef.current) {
          const d = Math.hypot(n.x - p.x, n.y - p.y);
          if (d < bestD) {
            bestD = d;
            bestId = n.id;
          }
        }
        setHovered(bestId);
      },
      onPointerLeave: () => setHovered(null),
      children: [
        centered && centered.id !== egoId && onResetFocus && /* @__PURE__ */ jsxs12("button", { type: "button", className: "iv-focus-crumb", onClick: () => onResetFocus(), children: [
          "\u25CE centered on ",
          labelOf(centered),
          " \u2014 back to you"
        ] }),
        children,
        showGalaxy && /* @__PURE__ */ jsx26(Suspense, { fallback: null, children: /* @__PURE__ */ jsx26(
          GalaxyLayer,
          {
            nodesRef,
            edgesRef,
            visuals: galaxyVisuals,
            tick,
            cam,
            w,
            h: H,
            opacity: galaxyOpacity,
            parchment: theme.star,
            edgeColor: theme.ring
          }
        ) }),
        galaxyHubs.length > 0 && /* @__PURE__ */ jsx26("div", { className: "iv-galaxy-labels", style: { opacity: galaxyOpacity }, children: galaxyHubs.map((n) => /* @__PURE__ */ jsx26(
          "span",
          {
            className: n.id === centerId ? "iv-galaxy-label iv-galaxy-label--center" : "iv-galaxy-label",
            style: { left: n.x * cam.k + cam.tx, top: n.y * cam.k + cam.ty + 7 },
            children: labelOf(n.node)
          },
          n.id
        )) }),
        showRich && /* @__PURE__ */ jsx26(
          "svg",
          {
            ref: svgRef,
            width: w,
            height: H,
            style: {
              display: "block",
              touchAction: "none",
              position: "relative",
              opacity: richOpacity,
              pointerEvents: richOpacity < 0.15 ? "none" : void 0
            },
            children: /* @__PURE__ */ jsxs12("g", { transform: worldTransform, children: [
              hopRings && Array.from({ length: outerHop }, (_, i) => i + 1).map((hop) => /* @__PURE__ */ jsxs12("g", { style: { pointerEvents: "none" }, children: [
                /* @__PURE__ */ jsx26("circle", { cx, cy, r: ringR(hop), fill: "none", stroke: theme.ring, strokeOpacity: 0.07, strokeWidth: 1 }),
                /* @__PURE__ */ jsxs12(
                  "text",
                  {
                    x: cx + ringR(hop) - 4,
                    y: cy - 5,
                    textAnchor: "end",
                    fill: theme.ring,
                    fillOpacity: 0.38,
                    style: { fontFamily: "var(--ae-font-mono, ui-monospace)", fontSize: "calc(10px * var(--ae-text-scale, 1))", letterSpacing: "0.18em", textTransform: "uppercase" },
                    children: [
                      hop === maxHop ? `${maxHop}+` : hop,
                      " ",
                      hop === 1 ? "hop" : "hops"
                    ]
                  }
                )
              ] }, `ring-${hop}`)),
              sectorRays && sectors.size > 1 && [...sectors.entries()].map(([name, sector]) => {
                const outer = ringR(outerHop);
                const bx = cx + Math.cos(sector.start) * outer;
                const by = cy + Math.sin(sector.start) * outer;
                const lx = cx + Math.cos(sector.mid) * (outer + 16);
                const ly = cy + Math.sin(sector.mid) * (outer + 16);
                const flip = Math.cos(sector.mid) < 0;
                const lit = (hoveredNode?.node.group ?? "other") === name;
                const hue = groupColorOf(name) ?? theme.sector;
                return /* @__PURE__ */ jsxs12("g", { style: { pointerEvents: "none" }, children: [
                  /* @__PURE__ */ jsx26("line", { x1: cx, y1: cy, x2: bx, y2: by, stroke: lit ? hue : theme.sector, strokeOpacity: lit ? 0.45 : 0.14, strokeDasharray: "2 6" }),
                  /* @__PURE__ */ jsx26(
                    "text",
                    {
                      x: lx,
                      y: ly,
                      textAnchor: flip ? "end" : "start",
                      dominantBaseline: "middle",
                      fill: hue,
                      fillOpacity: lit ? 1 : 0.65,
                      style: { fontFamily: "var(--ae-font-mono, ui-monospace)", fontSize: `calc(${lit ? 11 : 10}px * var(--ae-text-scale, 1))`, letterSpacing: "0.18em", textTransform: "uppercase" },
                      children: name
                    }
                  )
                ] }, `sector-${name}`);
              }),
              edgesRef.current.map((e, i) => {
                const a = byId.get(e.source);
                const b = byId.get(e.target);
                if (!a || !b) return null;
                if (cullOn && (Math.max(a.x, b.x) < visX1 || Math.min(a.x, b.x) > visX2 || Math.max(a.y, b.y) < visY1 || Math.min(a.y, b.y) > visY2))
                  return null;
                const inFocus = !!hovered && (e.source === hovered || e.target === hovered);
                const dimmed = !!hovered && !inFocus;
                const dash = e.kind === "inferred" ? "7 5" : e.kind === "pending" ? "2 5" : void 0;
                return /* @__PURE__ */ jsx26(
                  "line",
                  {
                    "data-edge": e.kind ?? "confirmed",
                    x1: a.x,
                    y1: a.y,
                    x2: b.x,
                    y2: b.y,
                    stroke: theme.ring,
                    strokeWidth: inFocus ? 2 : 1,
                    strokeOpacity: inFocus ? 0.95 : dimmed ? 0.06 : e.kind === "pending" ? 0.18 : 0.25,
                    strokeDasharray: dash
                  },
                  i
                );
              }),
              hoveredNode && edgesRef.current.filter((e) => (e.source === hovered || e.target === hovered) && e.label).map((e, i) => {
                const a = byId.get(e.source);
                const b = byId.get(e.target);
                if (!a || !b) return null;
                return /* @__PURE__ */ jsx26("text", { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 - 4, textAnchor: "middle", fill: theme.ring, style: { fontFamily: "var(--ae-font-mono, ui-monospace)", fontSize: "calc(10px * var(--ae-text-scale, 1))", pointerEvents: "none" }, children: e.label }, `l-${i}`);
              }),
              [...drawNodes].sort((a, b) => Number(a.id === egoId) - Number(b.id === egoId)).map((n) => {
                const isEgo = n.id === egoId;
                const inFocus = !focus || focus.has(n.id);
                const override = resolveOverride(n.node);
                const glow = override ?? (isEgo ? theme.focusHalo : n.node.glow ?? theme.starHalo);
                const core = override ?? (isEgo ? theme.focusCore : theme.star);
                const bright = isEgo ? 1 : n.node.brightness ?? 0.5;
                const glowSize = isEgo ? 26 : 1 + bright * 13;
                const fillOpacity = isEgo ? 1 : 0.22 + 0.78 * bright;
                const strokeOpacity = isEgo ? 1 : 0.3 + 0.7 * bright;
                const labelOpacity = isEgo ? 1 : 0.4 + 0.6 * bright;
                return /* @__PURE__ */ jsxs12(
                  "g",
                  {
                    transform: `translate(${n.x},${n.y})`,
                    "data-hop": hops.get(n.id) ?? maxHop,
                    opacity: inFocus ? 1 : 0.25,
                    style: { cursor: n.id === centerId ? "pointer" : "grab", transition: "opacity 120ms ease" },
                    onPointerDown: (ev) => {
                      ev.preventDefault();
                      dragRef.current = { id: n.id, sx: ev.clientX, sy: ev.clientY, moved: false, anchor: ev.currentTarget };
                    },
                    onPointerEnter: () => {
                      if (!dragRef.current) setHovered(n.id);
                    },
                    onPointerLeave: () => setHovered((h) => h === n.id ? null : h),
                    children: [
                      /* @__PURE__ */ jsx26(
                        "circle",
                        {
                          className: isEgo ? "iv-star iv-star--you" : "iv-star",
                          r: n.r,
                          fill: core,
                          fillOpacity,
                          stroke: glow,
                          strokeOpacity,
                          strokeWidth: isEgo ? 2 : 1,
                          filter: `drop-shadow(0 0 ${glowSize}px ${glow})`
                        }
                      ),
                      (whitelist ? labelled.has(n.id) : isEgo || labelled.has(n.id) || !!focus && focus.has(n.id)) && /* @__PURE__ */ jsx26(
                        "text",
                        {
                          y: n.r + 15 / labelShrink,
                          textAnchor: "middle",
                          fill: theme.label,
                          fillOpacity: labelOpacity,
                          style: { fontFamily: "var(--ae-font-mono, ui-monospace)", fontSize: `calc(${11 / labelShrink}px * var(--ae-text-scale, 1))`, letterSpacing: "0.02em", pointerEvents: "none" },
                          children: labelOf(n.node)
                        }
                      )
                    ]
                  },
                  n.id
                );
              })
            ] })
          }
        ),
        overlay && /* @__PURE__ */ jsx26("div", { className: "iv-constellation-overlay", "aria-hidden": "true", children: overlay((dx, dy) => projectCenterOffset(cam, world, tiltY, dx, dy), cam.k) }),
        timeControl && timeSpan && /* @__PURE__ */ jsxs12("div", { className: "iv-history", children: [
          /* @__PURE__ */ jsx26(
            "button",
            {
              type: "button",
              className: "iv-history-play",
              "aria-label": playing ? "Pause history" : "Play history from the beginning",
              onClick: () => {
                if (playing) setPlaying(false);
                else {
                  setAsOf(timeSpan.min);
                  setPlaying(true);
                }
              },
              children: playing ? "\u275A\u275A" : "\u25B6"
            }
          ),
          /* @__PURE__ */ jsx26(
            "input",
            {
              type: "range",
              className: "iv-history-slider",
              min: timeSpan.min,
              max: timeSpan.max,
              step: (timeSpan.max - timeSpan.min) / 200,
              value: asOf ?? timeSpan.max,
              onChange: (e) => {
                setPlaying(false);
                const v = Number(e.target.value);
                setAsOf(v >= timeSpan.max - 1e3 ? null : v);
              }
            }
          ),
          /* @__PURE__ */ jsx26("span", { className: "iv-history-label", children: asOf === null ? "now" : `as of ${new Date(asOf).toLocaleDateString()}` })
        ] }),
        hoveredNode && !dragRef.current && /* @__PURE__ */ jsxs12(
          "div",
          {
            className: "iv-hover-card",
            style: {
              left: Math.min(Math.max(hoveredNode.x * cam.k + cam.tx + 18, 8), w - 190),
              top: Math.min(Math.max((cy + (hoveredNode.y - cy) * tiltY) * cam.k + cam.ty - 14, 8), H - 96)
            },
            children: [
              /* @__PURE__ */ jsx26("div", { className: "iv-hover-card-name", children: labelOf(hoveredNode.node) }),
              (hoveredNode.node.meta || hoveredNode.node.group) && /* @__PURE__ */ jsxs12("div", { className: "iv-hover-card-row", children: [
                hoveredNode.node.meta,
                hoveredNode.node.group && /* @__PURE__ */ jsx26("span", { className: "iv-hover-card-group", style: { color: groupColorOf(hoveredNode.node.group) ?? void 0 }, children: hoveredNode.node.group })
              ] }),
              /* @__PURE__ */ jsxs12("div", { className: "iv-hover-card-row", children: [
                (hops.get(hoveredNode.id) ?? maxHop) === 0 ? "centre" : `${hops.get(hoveredNode.id)} hop${(hops.get(hoveredNode.id) ?? 0) > 1 ? "s" : ""} away`,
                " \xB7 ",
                degree.get(hoveredNode.id) ?? 0,
                " connection",
                (degree.get(hoveredNode.id) ?? 0) === 1 ? "" : "s"
              ] })
            ]
          }
        )
      ]
    }
  );
}

// src/components/Walkthrough/Walkthrough.tsx
import {
  useCallback as useCallback6,
  useEffect as useEffect8,
  useLayoutEffect as useLayoutEffect4,
  useRef as useRef8,
  useState as useState9
} from "react";
import { createPortal as createPortal6 } from "react-dom";

// src/components/Walkthrough/walkGeometry.ts
var TOOLTIP_GAP = 14;
var VIEWPORT_MARGIN4 = 12;
var SPOTLIGHT_PAD = 8;
function clampIndex2(index, length) {
  if (length <= 0) return 0;
  if (index < 0) return 0;
  if (index > length - 1) return length - 1;
  return index;
}
function nextIndex(index, length) {
  return clampIndex2(index + 1, length);
}
function prevIndex(index, length) {
  return clampIndex2(index - 1, length);
}
function isLastStep(index, length) {
  return length > 0 && index >= length - 1;
}
function progressLabel(index, length) {
  return `${clampIndex2(index, length) + 1} of ${length}`;
}
function resolveTarget(target, root = typeof document !== "undefined" ? document : { querySelector: () => null }) {
  try {
    if (typeof target === "function") return target() ?? null;
    return root.querySelector(target);
  } catch {
    return null;
  }
}
function cursorTarget(box) {
  return { x: box.left + box.width / 2, y: box.top + box.height / 2 };
}
function spotlightBox(box, viewport, pad = SPOTLIGHT_PAD) {
  const left = Math.max(0, box.left - pad);
  const top = Math.max(0, box.top - pad);
  const right = Math.min(viewport.width, box.left + box.width + pad);
  const bottom = Math.min(viewport.height, box.top + box.height + pad);
  return {
    left,
    top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top)
  };
}
function placeTooltip(target, tip, placement, viewport, gap = TOOLTIP_GAP, margin = VIEWPORT_MARGIN4) {
  const vw = viewport.width;
  const vh = viewport.height;
  const tRight = target.left + target.width;
  const tBottom = target.top + target.height;
  let resolved = placement;
  if (placement === "top") {
    const need = tip.height + gap + margin;
    if (target.top < need && vh - tBottom >= need) resolved = "bottom";
  } else if (placement === "bottom") {
    const need = tip.height + gap + margin;
    if (vh - tBottom < need && target.top >= need) resolved = "top";
  } else if (placement === "left") {
    const need = tip.width + gap + margin;
    if (target.left < need && vw - tRight >= need) resolved = "right";
  } else if (placement === "right") {
    const need = tip.width + gap + margin;
    if (vw - tRight < need && target.left >= need) resolved = "left";
  }
  let top;
  let left;
  switch (resolved) {
    case "top":
      top = target.top - gap - tip.height;
      left = target.left + target.width / 2 - tip.width / 2;
      break;
    case "bottom":
      top = tBottom + gap;
      left = target.left + target.width / 2 - tip.width / 2;
      break;
    case "left":
      left = target.left - gap - tip.width;
      top = target.top + target.height / 2 - tip.height / 2;
      break;
    case "right":
    default:
      left = tRight + gap;
      top = target.top + target.height / 2 - tip.height / 2;
      break;
  }
  top = Math.max(margin, Math.min(top, vh - tip.height - margin));
  left = Math.max(margin, Math.min(left, vw - tip.width - margin));
  return { top, left, placement: resolved };
}
function tweenDuration(reducedMotion, base) {
  return reducedMotion ? 0 : base;
}
function settleStep(steps, from, dir, resolve = (t) => resolveTarget(t)) {
  for (let i = from; i >= 0 && i < steps.length; i += dir) {
    if (resolve(steps[i].target)) return i;
  }
  return null;
}

// src/components/Walkthrough/Walkthrough.tsx
import { jsx as jsx27, jsxs as jsxs13 } from "react/jsx-runtime";
var CURSOR_TWEEN_MS = 620;
var RIPPLE_MS = 520;
function prefersReducedMotion() {
  if (typeof document !== "undefined") {
    if (document.documentElement.getAttribute("data-motion") === "reduced") return true;
  }
  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
    }
  }
  return false;
}
function viewportBox() {
  return {
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0
  };
}
function boxOf(el) {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}
function Walkthrough({
  steps,
  open,
  onClose,
  onStepChange,
  cursor = true,
  startIndex = 0,
  autoplay
}) {
  const [index, setIndex] = useState9(() => clampIndex2(startIndex, steps.length));
  const [target, setTarget] = useState9(null);
  const [tip, setTip] = useState9(null);
  const [cursorAt, setCursorAt] = useState9(null);
  const [ripple, setRipple] = useState9(0);
  const [playing, setPlaying] = useState9(Boolean(autoplay));
  const cardRef = useRef8(null);
  const dirRef = useRef8(1);
  const rippleTimer = useRef8(null);
  const reduced = open ? prefersReducedMotion() : false;
  const step = steps[index];
  const showCursor = (step?.showCursor ?? cursor) === true;
  useEffect8(() => {
    if (!open) return;
    setIndex(clampIndex2(startIndex, steps.length));
    setCursorAt(null);
    setPlaying(Boolean(autoplay));
  }, [open]);
  useEffect8(() => {
    if (!open || steps.length === 0) return;
    const resolve = (t) => resolveTarget(t);
    if (resolve(steps[index]?.target)) return;
    const found = settleStep(steps, index, dirRef.current, resolve) ?? settleStep(steps, index, dirRef.current === 1 ? -1 : 1, resolve);
    if (found == null) onClose();
    else if (found !== index) setIndex(found);
  }, [open, index, steps]);
  const measure = useCallback6(() => {
    const el = step ? resolveTarget(step.target) : null;
    if (!el) {
      setTarget(null);
      return;
    }
    const vp2 = viewportBox();
    const b = boxOf(el);
    setTarget(spotlightBox(b, vp2));
    const card = cardRef.current;
    const size = card ? { width: card.offsetWidth || 300, height: card.offsetHeight || 150 } : { width: 300, height: 150 };
    setTip(placeTooltip(spotlightBox(b, vp2), size, step?.placement ?? "bottom", vp2));
    if (showCursor) setCursorAt(cursorTarget(b));
  }, [step, showCursor]);
  useLayoutEffect4(() => {
    if (!open || !step) return;
    const el = resolveTarget(step.target);
    if (el && "scrollIntoView" in el) {
      el.scrollIntoView({
        block: "center",
        inline: "center",
        behavior: reduced ? "auto" : "smooth"
      });
    }
    measure();
    const raf = requestAnimationFrame(measure);
    onStepChange?.(index);
    if (showCursor) {
      if (rippleTimer.current) clearTimeout(rippleTimer.current);
      const delay = tweenDuration(reduced, CURSOR_TWEEN_MS);
      rippleTimer.current = setTimeout(() => setRipple((n) => n + 1), delay + 20);
    }
    return () => {
      cancelAnimationFrame(raf);
      if (rippleTimer.current) clearTimeout(rippleTimer.current);
    };
  }, [open, index, step?.target]);
  useEffect8(() => {
    if (!open) return;
    const onMove = () => measure();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, measure]);
  useEffect8(() => {
    if (open && tip) cardRef.current?.focus();
  }, [open, index, Boolean(tip)]);
  const goNext = useCallback6(() => {
    dirRef.current = 1;
    setIndex((i) => {
      if (isLastStep(i, steps.length)) {
        onClose();
        return i;
      }
      return nextIndex(i, steps.length);
    });
  }, [steps.length, onClose]);
  const goPrev = useCallback6(() => {
    dirRef.current = -1;
    setIndex((i) => prevIndex(i, steps.length));
  }, [steps.length]);
  useEffect8(() => {
    if (!open || !autoplay || !playing) return;
    if (isLastStep(index, steps.length)) return;
    const id = setTimeout(goNext, autoplay.stepMs);
    return () => clearTimeout(id);
  }, [open, autoplay, playing, index, steps.length, goNext]);
  const onKeyDown = useCallback6(
    (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      } else if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
    },
    [onClose, goNext, goPrev]
  );
  if (!open || typeof document === "undefined" || steps.length === 0) return null;
  const vp = viewportBox();
  const last = isLastStep(index, steps.length);
  const tweenMs = tweenDuration(reduced, CURSOR_TWEEN_MS);
  const cardStyle = tip ? { top: tip.top, left: tip.left, visibility: "visible" } : { top: 0, left: 0, visibility: "hidden" };
  const cursorStyle = cursorAt ? {
    transform: `translate(${cursorAt.x}px, ${cursorAt.y}px)`,
    transitionDuration: `${tweenMs}ms`,
    opacity: 1
  } : { opacity: 0 };
  return createPortal6(
    /* @__PURE__ */ jsxs13("div", { className: "iv-walk", role: "dialog", "aria-modal": "true", "aria-label": "Guided tour", children: [
      /* @__PURE__ */ jsxs13(
        "svg",
        {
          className: "iv-walk__scrim",
          width: vp.width,
          height: vp.height,
          onClick: onClose,
          "aria-hidden": "true",
          children: [
            /* @__PURE__ */ jsx27("defs", { children: /* @__PURE__ */ jsxs13("mask", { id: "iv-walk-mask", children: [
              /* @__PURE__ */ jsx27("rect", { x: "0", y: "0", width: vp.width, height: vp.height, fill: "white" }),
              target && /* @__PURE__ */ jsx27(
                "rect",
                {
                  className: "iv-walk__hole",
                  x: target.left,
                  y: target.top,
                  width: target.width,
                  height: target.height,
                  rx: "10",
                  fill: "black"
                }
              )
            ] }) }),
            /* @__PURE__ */ jsx27(
              "rect",
              {
                x: "0",
                y: "0",
                width: vp.width,
                height: vp.height,
                className: "iv-walk__dim",
                mask: "url(#iv-walk-mask)"
              }
            ),
            target && /* @__PURE__ */ jsx27(
              "rect",
              {
                className: "iv-walk__ring",
                x: target.left,
                y: target.top,
                width: target.width,
                height: target.height,
                rx: "10",
                fill: "none"
              }
            )
          ]
        }
      ),
      showCursor && /* @__PURE__ */ jsxs13("div", { className: "iv-walk__cursor", style: cursorStyle, "aria-hidden": "true", children: [
        !reduced && /* @__PURE__ */ jsx27("span", { className: "iv-walk__ripple", style: { animationDuration: `${RIPPLE_MS}ms` } }, ripple),
        /* @__PURE__ */ jsx27("svg", { width: "26", height: "26", viewBox: "0 0 26 26", className: "iv-walk__pointer", children: /* @__PURE__ */ jsx27(
          "path",
          {
            d: "M4 3l7.5 18 2.6-7.4 7.4-2.6L4 3z",
            fill: "var(--ae-color-bg-inverse)",
            stroke: "var(--ae-color-text-inverse)",
            strokeWidth: "1.5",
            strokeLinejoin: "round"
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs13(
        "div",
        {
          ref: cardRef,
          className: `iv-walk__card iv-walk__card--${tip?.placement ?? "bottom"}`,
          style: cardStyle,
          tabIndex: -1,
          onKeyDown,
          role: "group",
          "aria-label": typeof step?.title === "string" ? step.title : "Tour step",
          children: [
            /* @__PURE__ */ jsxs13("div", { className: "iv-walk__card-head", children: [
              /* @__PURE__ */ jsx27("span", { className: "iv-walk__count", children: progressLabel(index, steps.length) }),
              /* @__PURE__ */ jsx27("button", { type: "button", className: "iv-walk__close", "aria-label": "End tour", onClick: onClose, children: /* @__PURE__ */ jsx27("svg", { width: "12", height: "12", viewBox: "0 0 12 12", "aria-hidden": "true", children: /* @__PURE__ */ jsx27("path", { d: "M1 1l10 10M11 1L1 11", stroke: "currentColor", strokeWidth: "1.4", strokeLinecap: "round" }) }) })
            ] }),
            step?.title && /* @__PURE__ */ jsx27("div", { className: "iv-walk__title", children: step.title }),
            step?.body && /* @__PURE__ */ jsx27("div", { className: "iv-walk__body", children: step.body }),
            /* @__PURE__ */ jsx27("div", { className: "iv-walk__dots", "aria-hidden": "true", children: steps.map((_, i) => /* @__PURE__ */ jsx27("span", { className: `iv-walk__dot${i === index ? " iv-walk__dot--on" : ""}` }, i)) }),
            /* @__PURE__ */ jsxs13("div", { className: "iv-walk__actions", children: [
              autoplay && /* @__PURE__ */ jsx27(
                "button",
                {
                  type: "button",
                  className: "iv-walk__btn iv-walk__btn--ghost",
                  onClick: () => last ? (setIndex(clampIndex2(startIndex, steps.length)), setPlaying(true)) : setPlaying((p) => !p),
                  "aria-label": last ? "Replay tour" : playing ? "Pause tour" : "Play tour",
                  children: last ? "\u21BA Replay" : playing ? "\u275A\u275A Pause" : "\u25B6 Play"
                }
              ),
              /* @__PURE__ */ jsx27("span", { className: "iv-walk__spacer" }),
              /* @__PURE__ */ jsx27(
                "button",
                {
                  type: "button",
                  className: "iv-walk__btn iv-walk__btn--ghost",
                  onClick: goPrev,
                  disabled: index === 0,
                  children: "Back"
                }
              ),
              /* @__PURE__ */ jsx27("button", { type: "button", className: "iv-walk__btn iv-walk__btn--primary", onClick: goNext, children: last ? "Done" : "Next" })
            ] })
          ]
        }
      )
    ] }),
    document.body
  );
}

// src/components/Checkbox/Checkbox.tsx
import {
  useEffect as useEffect9,
  useRef as useRef9
} from "react";
import { jsx as jsx28, jsxs as jsxs14 } from "react/jsx-runtime";
function Checkbox({
  checked,
  onChange,
  label,
  indeterminate = false,
  disabled = false,
  className,
  ...props
}) {
  const inputRef = useRef9(null);
  useEffect9(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);
  const classes = ["iv-checkbox", disabled && "iv-checkbox--disabled", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs14("label", { className: classes, children: [
    /* @__PURE__ */ jsx28(
      "input",
      {
        ...props,
        ref: inputRef,
        type: "checkbox",
        className: "iv-checkbox__input",
        checked,
        disabled,
        "aria-checked": indeterminate ? "mixed" : checked,
        onChange: (e) => onChange?.(e.target.checked, e)
      }
    ),
    /* @__PURE__ */ jsx28("span", { className: "iv-checkbox__box", "aria-hidden": "true", children: /* @__PURE__ */ jsxs14("svg", { className: "iv-checkbox__check", viewBox: "0 0 16 16", fill: "none", children: [
      /* @__PURE__ */ jsx28(
        "path",
        {
          className: "iv-checkbox__check-tick",
          d: "M3.5 8.5L6.5 11.5L12.5 4.5",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx28(
        "path",
        {
          className: "iv-checkbox__check-dash",
          d: "M4 8H12",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round"
        }
      )
    ] }) }),
    label != null && /* @__PURE__ */ jsx28("span", { className: "iv-checkbox__label", children: label })
  ] });
}

// src/components/Heading/Heading.tsx
import { jsx as jsx29 } from "react/jsx-runtime";
var DEFAULT_TAG = {
  1: "h1",
  2: "h2",
  3: "h3",
  4: "h4"
};
function Heading({
  level = 2,
  as,
  className,
  ...props
}) {
  const Component = as ?? DEFAULT_TAG[level];
  const classes = ["iv-heading", `iv-heading--${level}`, className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx29(Component, { ...props, className: classes });
}

// src/components/StatusPip/StatusPip.tsx
import { jsx as jsx30 } from "react/jsx-runtime";
function StatusPip({ status, label, className, ...props }) {
  const classes = ["iv-pip", `iv-pip--${status}`, className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx30("span", { role: "img", "aria-label": label ?? status, ...props, className: classes });
}

// src/components/MonoStat/MonoStat.tsx
import { jsx as jsx31, jsxs as jsxs15 } from "react/jsx-runtime";
function MonoStat({ value, label, delta, className, ...props }) {
  const classes = ["iv-monostat", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs15("div", { ...props, className: classes, children: [
    /* @__PURE__ */ jsx31("div", { className: "iv-monostat-value", children: value }),
    /* @__PURE__ */ jsx31("div", { className: "iv-monostat-label", children: label }),
    delta !== void 0 && /* @__PURE__ */ jsx31("div", { className: "iv-monostat-delta", children: delta })
  ] });
}

// src/components/Tile/Tile.tsx
import { jsx as jsx32, jsxs as jsxs16 } from "react/jsx-runtime";
function formatAsOf(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function Tile({
  title,
  status,
  asOf,
  href,
  linkLabel,
  className,
  children,
  ...props
}) {
  const classes = ["iv-tile", status ? `iv-tile--${status}` : "", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs16("section", { ...props, className: classes, children: [
    /* @__PURE__ */ jsxs16("header", { className: "iv-tile-header", children: [
      status && /* @__PURE__ */ jsx32(StatusPip, { status }),
      /* @__PURE__ */ jsx32("h2", { className: "iv-tile-title", children: title }),
      /* @__PURE__ */ jsxs16("div", { className: "iv-tile-meta", children: [
        asOf && /* @__PURE__ */ jsx32("time", { className: "iv-tile-asof", dateTime: asOf, children: formatAsOf(asOf) }),
        href && /* @__PURE__ */ jsxs16("a", { className: "iv-tile-link", href, target: "_blank", rel: "noreferrer", children: [
          linkLabel ?? "open",
          " \u2197"
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx32("div", { className: "iv-tile-body", children })
  ] });
}

// src/components/StatusChip/StatusChip.tsx
import { jsx as jsx33 } from "react/jsx-runtime";
var STATUS_CHIP_LABEL = {
  triage: "Triage",
  todo: "Todo",
  open: "Open",
  reopened: "Reopened",
  "confirm-proposed": "Confirm proposed",
  closed: "Closed",
  canceled: "Canceled"
};
function StatusChip({ status, label, className, ...props }) {
  const classes = ["iv-status-chip", `iv-status-chip--${status}`, className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx33("span", { ...props, className: classes, children: label ?? STATUS_CHIP_LABEL[status] });
}

// src/components/CommandPalette/CommandPalette.tsx
import {
  useEffect as useEffect10,
  useId as useId5,
  useMemo as useMemo4,
  useRef as useRef10,
  useState as useState10
} from "react";
import { createPortal as createPortal7 } from "react-dom";
import { Fragment as Fragment2, jsx as jsx34, jsxs as jsxs17 } from "react/jsx-runtime";
function rankPaletteItem(item, query) {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const ids = [item.id, ...item.keywords ?? []].map((s) => s.toLowerCase());
  if (ids.some((s) => s === q)) return 0;
  if (ids.some((s) => s.includes(q))) return 1;
  const title = item.title.toLowerCase();
  if (title.startsWith(q)) return 2;
  if (title.includes(q)) return 3;
  return -1;
}
function CommandPalette({
  open,
  onClose,
  items,
  onSelect,
  placeholder = "Jump to\u2026",
  emptyLabel = "No matches",
  maxResults = 8,
  rank = rankPaletteItem,
  renderItem,
  ariaLabel = "Command palette"
}) {
  const inputRef = useRef10(null);
  const listRef = useRef10(null);
  const listId = useId5();
  const [query, setQuery] = useState10("");
  const [active, setActive] = useState10(0);
  const results = useMemo4(() => {
    const q = query.trim();
    if (!q) return items.slice(0, maxResults);
    return items.map((item) => ({ item, score: rank(item, q) })).filter((r) => r.score >= 0).sort((a, b) => a.score - b.score).slice(0, maxResults).map((r) => r.item);
  }, [items, query, maxResults, rank]);
  useEffect10(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);
  useEffect10(() => {
    setActive(0);
  }, [query]);
  useEffect10(() => {
    if (!open) return;
    const el = listRef.current?.querySelector('[data-active="true"]');
    el?.scrollIntoView?.({ block: "nearest" });
  }, [active, open, results.length]);
  if (!open) return null;
  const clamped = Math.min(active, Math.max(0, results.length - 1));
  const choose = (item) => {
    if (!item) return;
    onClose();
    onSelect(item);
  };
  const onKeyDown = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (results.length) setActive((i) => (i + 1) % results.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        if (results.length) setActive((i) => (i - 1 + results.length) % results.length);
        break;
      case "Home":
        e.preventDefault();
        setActive(0);
        break;
      case "End":
        e.preventDefault();
        if (results.length) setActive(results.length - 1);
        break;
      case "Enter":
        e.preventDefault();
        choose(results[clamped]);
        break;
      case "Escape":
        e.preventDefault();
        onClose();
        break;
      case "Tab":
        e.preventDefault();
        break;
    }
  };
  const activeId = results[clamped] ? `${listId}-opt-${results[clamped].id}` : void 0;
  return createPortal7(
    /* @__PURE__ */ jsx34(
      "div",
      {
        className: "iv-cmdk-scrim",
        onMouseDown: (e) => {
          if (e.target === e.currentTarget) onClose();
        },
        children: /* @__PURE__ */ jsxs17("div", { className: "iv-cmdk", role: "dialog", "aria-modal": "true", "aria-label": ariaLabel, children: [
          /* @__PURE__ */ jsxs17("div", { className: "iv-cmdk-search", children: [
            /* @__PURE__ */ jsx34("span", { className: "iv-cmdk-search-glyph", "aria-hidden": "true", children: "\u2315" }),
            /* @__PURE__ */ jsx34(
              "input",
              {
                ref: inputRef,
                className: "iv-cmdk-input",
                type: "text",
                role: "combobox",
                "aria-expanded": "true",
                "aria-controls": listId,
                "aria-activedescendant": activeId,
                "aria-autocomplete": "list",
                "aria-label": ariaLabel,
                placeholder,
                value: query,
                onChange: (e) => setQuery(e.target.value),
                onKeyDown,
                autoComplete: "off",
                spellCheck: false
              }
            ),
            /* @__PURE__ */ jsx34("span", { className: "iv-cmdk-hintkey", "aria-hidden": "true", children: "esc" })
          ] }),
          results.length === 0 ? /* @__PURE__ */ jsx34("div", { className: "iv-cmdk-empty", children: emptyLabel }) : /* @__PURE__ */ jsx34("div", { className: "iv-cmdk-list", role: "listbox", id: listId, "aria-label": ariaLabel, ref: listRef, children: results.map((item, i) => {
            const isActive = i === clamped;
            return /* @__PURE__ */ jsx34(
              "button",
              {
                type: "button",
                id: `${listId}-opt-${item.id}`,
                role: "option",
                "aria-selected": isActive,
                "data-active": isActive,
                tabIndex: -1,
                className: `iv-cmdk-item${isActive ? " iv-cmdk-item--active" : ""}`,
                onMouseMove: () => setActive(i),
                onClick: () => choose(item),
                children: renderItem ? renderItem(item, isActive) : /* @__PURE__ */ jsxs17(Fragment2, { children: [
                  /* @__PURE__ */ jsx34("span", { className: "iv-cmdk-item-title", children: item.title }),
                  item.hint && /* @__PURE__ */ jsx34("span", { className: "iv-cmdk-item-hint", children: item.hint })
                ] })
              },
              item.id
            );
          }) })
        ] })
      }
    ),
    document.body
  );
}

// src/components/KeyList/useKeyList.ts
import { useEffect as useEffect11, useRef as useRef11, useState as useState11 } from "react";
function isTypingTarget(target) {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement || target instanceof HTMLElement && target.isContentEditable;
}
function useKeyList({
  count,
  enabled = true,
  onActivate,
  onSelectionChange,
  onFilterKey
}) {
  const [selected, setSelected] = useState11(0);
  const rows = useRef11([]);
  const clamped = Math.min(selected, Math.max(0, count - 1));
  const state = useRef11({ clamped, count, onActivate, onSelectionChange, onFilterKey });
  state.current = { clamped, count, onActivate, onSelectionChange, onFilterKey };
  useEffect11(() => {
    if (!enabled) return;
    const onKeyDown = (e) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      const { clamped: current, count: n, onActivate: activate, onSelectionChange: changed, onFilterKey: filter } = state.current;
      const move = (delta) => {
        if (n === 0) return;
        const next = Math.min(Math.max(0, current + delta), n - 1);
        const row = rows.current[next];
        row?.focus();
        row?.scrollIntoView?.({ block: "nearest" });
        setSelected(next);
        if (next !== current) changed?.(next);
      };
      switch (e.key) {
        case "j":
        case "ArrowDown":
          e.preventDefault();
          move(1);
          break;
        case "k":
        case "ArrowUp":
          e.preventDefault();
          move(-1);
          break;
        case "Enter":
          if (e.target instanceof HTMLButtonElement) return;
          if (n === 0) return;
          e.preventDefault();
          activate?.(current);
          break;
        case "/":
          if (!filter) return;
          e.preventDefault();
          filter();
          break;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
  return {
    selected: clamped,
    select: (index) => {
      setSelected(index);
      state.current.onSelectionChange?.(index);
    },
    registerRow: (index) => (el) => {
      rows.current[index] = el;
    }
  };
}

// src/usePanelState.ts
import { useCallback as useCallback7, useState as useState12 } from "react";
function usePanelState(persistKey, defaultCollapsed = false) {
  const [collapsed, setCollapsed] = useState12(() => {
    if (typeof window === "undefined" || !window.localStorage) return defaultCollapsed;
    const stored = window.localStorage.getItem(persistKey);
    return stored === null ? defaultCollapsed : stored === "1";
  });
  const toggle = useCallback7(() => {
    setCollapsed((v) => {
      const next = !v;
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(persistKey, next ? "1" : "0");
      }
      return next;
    });
  }, [persistKey]);
  return [collapsed, toggle];
}

// src/components/Tabs/Tabs.tsx
import { jsx as jsx35, jsxs as jsxs18 } from "react/jsx-runtime";
function Tabs({ items, activeKey, onChange, ariaLabel }) {
  return /* @__PURE__ */ jsx35("div", { className: "iv-tabs", role: "tablist", "aria-label": ariaLabel, children: items.map((t) => {
    const active = t.key === activeKey;
    const hasBadge = t.badge !== void 0 && t.badge !== null && t.badge !== "" && t.badge !== 0;
    const classes = [
      "iv-tab",
      active && "iv-tab--active",
      hasBadge && "iv-tab--alert"
    ].filter(Boolean).join(" ");
    return /* @__PURE__ */ jsxs18(
      "button",
      {
        type: "button",
        role: "tab",
        "aria-selected": active,
        className: classes,
        onClick: () => onChange(t.key),
        children: [
          t.icon && /* @__PURE__ */ jsx35("span", { className: "iv-tab__icon", children: t.icon }),
          t.label,
          hasBadge && /* @__PURE__ */ jsx35("span", { className: "iv-tab__badge", children: t.badge })
        ]
      },
      t.key
    );
  }) });
}

// src/components/PanelFold/PanelFold.tsx
import { jsx as jsx36, jsxs as jsxs19 } from "react/jsx-runtime";
function PanelFold({ side, collapsed, onToggle, label, badge }) {
  const hasBadge = badge !== void 0 && badge !== null && badge !== "" && badge !== 0;
  const classes = [
    "iv-panel-fold",
    `iv-panel-fold--${side}`,
    hasBadge && "iv-panel-fold--alert"
  ].filter(Boolean).join(" ");
  const chevron = side === "left" ? collapsed ? "\u203A" : "\u2039" : collapsed ? "\u2039" : "\u203A";
  return /* @__PURE__ */ jsxs19(
    "button",
    {
      type: "button",
      className: classes,
      "aria-label": hasBadge ? `${label} (${badge})` : label,
      "aria-expanded": !collapsed,
      onClick: onToggle,
      children: [
        /* @__PURE__ */ jsx36("span", { className: "iv-panel-fold__chevron", "aria-hidden": "true", children: chevron }),
        hasBadge && /* @__PURE__ */ jsx36("span", { className: "iv-panel-fold__badge", children: badge })
      ]
    }
  );
}
export {
  ACCENTS,
  AppNav,
  AppShell,
  AppearancePanel,
  Badge,
  Button,
  Calendar,
  Card,
  ChatSurface,
  Checkbox,
  CommandPalette,
  Constellation,
  DEFAULT_LIVENESS_THRESHOLDS,
  DEFAULT_MAX_HOP,
  DatePicker,
  Dialog,
  DEFAULT_THRESHOLDS as HEATMAP_DEFAULT_THRESHOLDS,
  Heading,
  Heatmap,
  Input,
  ItemCard,
  MonoStat,
  NavItem,
  PanelFold,
  SKY_MARGIN,
  STATUS_CHIP_LABEL,
  ScrollArea,
  Select,
  Spinner,
  Stack,
  StatusChip,
  StatusDot,
  StatusPip,
  Surface,
  TEXT_SCALES,
  THEME_PRESETS,
  Table,
  Tabs,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  ThemeToggle,
  Tile,
  Tooltip,
  Tr,
  Walkthrough,
  angleDelta,
  applyAccent,
  applyAccentSwatch,
  applyColorMode,
  applyContrast,
  applyDensity,
  applyFontPreset,
  applyMotion,
  applyTextScale,
  applyThemePreset,
  buildHeatmapGrid,
  clampIndex2 as clampIndex,
  clearAccent,
  computeRingRadii,
  cursorTarget,
  filterByReach,
  filterByTime,
  fitCamera,
  fitCameraZoomed,
  freshness,
  getAccent,
  getColorMode,
  getContrast,
  getDensity,
  getFontPreset,
  getMotion,
  getTextScale,
  getThemePreset,
  hopDistances,
  initAccent,
  initColorMode,
  initContrast,
  initDensity,
  initFontPreset,
  initMotion,
  initTextScale,
  initThemePreset,
  isLastStep,
  levelFor,
  livenessState,
  nextIndex,
  placeTooltip,
  presetNativeMode,
  prevIndex,
  progressLabel,
  projectCenterOffset,
  rankPaletteItem,
  resolveTarget,
  screenToWorld,
  sectorsByGroup,
  setAccent,
  setColorMode,
  setContrast,
  setDensity,
  setFontPreset,
  setMotion,
  setTextScale,
  setThemePreset,
  settleStep,
  spotlightBox,
  tiltFactor,
  toggleColorMode,
  toggleContrast,
  toggleDensity,
  toggleMotion,
  tweenDuration,
  useKeyList,
  usePanelState,
  zoomAround
};
